/**
 * Map generation from seed: planet placement, player spawn placement.
 * Pure functions — deterministic given the same seed.
 */

import type { PlanetState, PlayerState, Vec3, PlanetArchetype, PlayerLoadout, BotConfig, HazardState, RuinState, RuinType, FloraState, FloraType, RockState, RockType, EnvironmentalEffectState, EnvironmentalEffectType, PlanetSizePreset, MapLayout, StarType, OrbitalZone, PlanetSizeCategory, CarryableObjectState, CollectibleNode, ModeConfig, TerrainProfile, TerrainStampConfig, TerrainStampType, SystemPreset } from '../types';
import { createSandboxLoadout } from '../types';
import { getModeRules } from './modeRules';
import { PLANET_PALETTES, TEAMS, BASE_MOVEMENT_RANGE, STARTING_FLAGS, STAR_DENSITY, STAR_RADIUS_MIN, STAR_RADIUS_MAX, STAR_MIN_PLANET_DISTANCE_MULT, STAR_COLORS, WORMHOLE_RADIUS, WORMHOLE_DENSITY, WORMHOLE_COLORS, BOT_PRESETS, BOT_SKILL_LEVELS, UNIT_SUFFIXES, PLANET_ARCHETYPES, HAZARD_HEALTH, HAZARD_COUNTS, HAZARD_MIN_SPAWN_ANGLE, RUIN_COUNTS, RUIN_MIN_SPAWN_ANGLE, ALL_RUIN_TYPES, FLORA_COUNTS, FLORA_MIN_SPAWN_ANGLE, ROCK_COUNTS, ROCK_MIN_SPAWN_ANGLE, EFFECT_COUNTS, PLANET_RING_CHANCE, STAR_TYPES, BIOME_AFFINITY, ORBITAL_BIOME_MODIFIERS, PLANET_SIZE_WEIGHTS, SIZE_CATEGORY_RADIUS, FITNESS_MOVEMENT_BONUS, JUMP_HEIGHT_BONUS, TERRAIN_PROFILES, SYSTEM_PRESETS, type RuinWeight, type FloraWeight, type RockWeight, type EffectWeight, MAX_TERRAIN_HEIGHT_FRACTION, MAX_CRATER_DEPTH_FRACTION } from '../constants';
import { plannedLandforms } from './landforms';
import { plannedErosion, type ErosionDial } from './erosionCarve';
import { latticeJitter, facetAngleOf, GROUND_JITTER, type GroundJitter } from './groundNoise';
import type { UnitSlotConfig } from '../types';
import { SeededRandom } from './random';
import { defaultUnitKit } from './unitModifiers';
import { vecLength, vecSub } from '../math';
import { dacos, dasin, dcos, dexp, dpow, dsin } from './dmath';

// =============================================================================
// MAP GENERATION
// =============================================================================

export interface GeneratedMap {
  planets: PlanetState[];
  players: PlayerState[];
  hazards: HazardState[];
  ruins: RuinState[];
  flora: FloraState[];
  rocks: RockState[];
  environmentalEffects: EnvironmentalEffectState[];
  carryables: CarryableObjectState[];
  collectibles: CollectibleNode[];
}

export interface BotOverride {
  difficulty?: string;
  personality?: string;
}

/**
 * Palettes a regular planet may cycle through. PLANET_PALETTES also holds the
 * megaplanet 'earth' palette, which has no PLANET_ARCHETYPES entry — cycling by
 * index over the full list reached it on the 10th planet of a starless map and
 * createPlanetState crashed reading its density range.
 */
const ARCHETYPE_PALETTES = PLANET_PALETTES.filter(p => p.name in PLANET_ARCHETYPES);

export function generateMap(
  numPlanets: number,
  numPlayers: number,
  numBots: number,
  seed: string,
  playerNames?: string[],
  playerLoadouts?: PlayerLoadout[],
  numStars: number = 0,
  numWormholePairs: number = 0,
  botDifficulty: string = 'medium',
  botPersonality: string = 'balanced',
  botOverrides?: BotOverride[],
  teamIndices?: number[],
  unitsPerTeam: number = 1,
  botUnitsPerTeam: number = 1,
  numTeams?: number,
  humanTeams?: number,
  botTeams?: number,
  unitSlots?: UnitSlotConfig[][],
  hazardDensity: 'none' | 'some' | 'many' = 'some',
  ruinDensity: 'none' | 'few' | 'some' | 'many' = 'some',
  ruinOverrides?: RuinOverride[],
  planetSize: PlanetSizePreset = 'mixed',
  mapLayout: MapLayout = 'random',
  starType?: StarType,
  startingCBP: number = 12,
  botStartingCBP?: number,
  systemPreset?: SystemPreset,
  /** 67.41: the Lab's Planet row — every planet this generation makes takes this kind (an action match has one); absent = the star-driven pick, unchanged */
  planetArchetype?: PlanetArchetype,
  /** 74.13 step 3: the Lab's Landforms row — every planet takes the landform pass (core/landforms.ts) before anything is placed on it; absent = today's ground */
  landforms?: boolean,
  /** 74.15 (DECISIONS 387): the Lab's Finer mesh row — every planet one icosphere subdivision finer (4× the facets); absent = today's mesh */
  finerMesh?: boolean,
  /** 74.19 (DECISIONS 435): the Lab's Erosion row — every planet carved by its own drainage (core/erosionCarve.ts); absent or off = today's ground */
  erosion?: ErosionDial,
  /** 74.22 stage 1 (DECISIONS 443): the Lab's Jitter row — the generator's roughness as a band-limited field (core/groundNoise.ts); absent or vertex = today's per-vertex hash in every byte */
  groundJitter?: GroundJitter,
): GeneratedMap {
  forcedArchetype = planetArchetype;
  landformsOn = landforms === true;
  finerMeshOn = finerMesh === true;
  erosionDial = erosion ?? 'off';
  groundJitterMode = groundJitter ?? 'vertex';
  try {
    return generateMapWith(numPlanets, numPlayers, numBots, seed, playerNames, playerLoadouts, numStars, numWormholePairs, botDifficulty, botPersonality, botOverrides, teamIndices, unitsPerTeam, botUnitsPerTeam, numTeams, humanTeams, botTeams, unitSlots, hazardDensity, ruinDensity, ruinOverrides, planetSize, mapLayout, starType, startingCBP, botStartingCBP, systemPreset);
  } finally {
    forcedArchetype = undefined;
    landformsOn = false;
    finerMeshOn = false;
    erosionDial = 'off';
    groundJitterMode = 'vertex';
  }
}

/** 67.41: the planet kind forced for the generation under way (generateMap sets it and clears it after; createPlanetState honours it). */
let forcedArchetype: PlanetArchetype | undefined;
/** 74.13 step 3: the landform pass for the generation under way (generateMap sets it and clears it after; generateMapWith honours it). */
let landformsOn = false;
/** 74.15: one subdivision finer for the generation under way (generateMap sets it and clears it after; createPlanetState honours it). */
let finerMeshOn = false;
/** 74.19: the erosion carving's dial for the generation under way (generateMap sets it and clears it after; generateMapWith honours it). */
let erosionDial: ErosionDial = 'off';
/** 74.22 stage 1: the generator's jitter for the generation under way (generateMap sets it and clears it after; generatePlanetGeometry honours it). */
let groundJitterMode: GroundJitter = 'vertex';

function generateMapWith(
  numPlanets: number, numPlayers: number, numBots: number, seed: string, playerNames: string[] | undefined, playerLoadouts: PlayerLoadout[] | undefined,
  numStars: number, numWormholePairs: number, botDifficulty: string, botPersonality: string, botOverrides: BotOverride[] | undefined, teamIndices: number[] | undefined,
  unitsPerTeam: number, botUnitsPerTeam: number, numTeams: number | undefined, humanTeams: number | undefined, botTeams: number | undefined, unitSlots: UnitSlotConfig[][] | undefined,
  hazardDensity: 'none' | 'some' | 'many', ruinDensity: 'none' | 'few' | 'some' | 'many', ruinOverrides: RuinOverride[] | undefined, planetSize: PlanetSizePreset, mapLayout: MapLayout,
  starType: StarType | undefined, startingCBP: number, botStartingCBP: number | undefined, systemPreset: SystemPreset | undefined,
): GeneratedMap {
  const rng = new SeededRandom(seed);

  // Apply system preset overrides (safety net if dropdown didn't have matching option)
  if (systemPreset && systemPreset !== 'custom') {
    const preset = SYSTEM_PRESETS[systemPreset];
    if (preset) {
      if (isNaN(numPlanets) || numPlanets < 1) numPlanets = preset.numPlanets;
      if (isNaN(numStars)) numStars = preset.numStars;
      if (isNaN(numWormholePairs)) numWormholePairs = preset.numWormholePairs;
      if (!planetSize || !['small', 'mixed', 'large', 'megaplanet'].includes(planetSize)) {
        planetSize = preset.planetSize as PlanetSizePreset;
      }
    }
  }

  // Generate stars first (they go at the front of the planets array)
  const stars = generateStars(numStars, rng, starType);
  const radiusRange = getRadiusRange(planetSize);
  const wormholes = generateWormholes(numWormholePairs, rng, stars, numPlanets, radiusRange);
  // When planetSize is 'megaplanet', don't pass starType — it would override the megaplanet radius range
  const effectiveStarType = planetSize === 'megaplanet' ? undefined : starType;
  const planets = generatePlanets(numPlanets, rng, [...stars, ...wormholes], planetSize, mapLayout, effectiveStarType);
  let allBodies = [...stars, ...wormholes, ...planets];

  // Re-index planet IDs to be sequential
  // Build old→new ID map for wormhole linkedWormholeId fixup
  const oldIdToNew = new Map<number, number>();
  for (let i = 0; i < allBodies.length; i++) {
    oldIdToNew.set(allBodies[i].id, i);
    allBodies[i] = { ...allBodies[i], id: i };
  }

  // Fix up wormhole linkedWormholeId references after re-indexing
  for (let i = 0; i < allBodies.length; i++) {
    if (allBodies[i].type === 'wormhole' && allBodies[i].linkedWormholeId != null) {
      const newId = oldIdToNew.get(allBodies[i].linkedWormholeId!);
      if (newId != null) {
        allBodies[i] = { ...allBodies[i], linkedWormholeId: newId };
      }
    }
  }

  // Post-generation biome diversity pass (only when star-driven)
  if (starType) {
    allBodies = ensureBiomeDiversity(allBodies, starType, rng);
  }

  // Earth preset: override first planet's heightmap and palette
  if (systemPreset === 'earth') {
    const earthIdx = allBodies.findIndex(p => p.type === 'planet');
    if (earthIdx >= 0) {
      const planet = allBodies[earthIdx];
      const earthHeightmap = generateEarthHeightmap(planet.basePositions, planet.radius, rng);
      const earthPalette = PLANET_PALETTES.find(p => p.name === 'earth') ?? PLANET_PALETTES[0];
      allBodies[earthIdx] = {
        ...planet,
        heightmap: earthHeightmap,
        initialHeightmap: earthHeightmap,
        palette: earthPalette,
        archetype: 'pelago',
      };
    }
  }

  // 74.13 step 3: the landforms, once each planet's kind is final and before anything stands on the ground (the pass draws
  // nothing from this generation's rng: everything after takes the same draws on the new ground)
  // 74.19: the erosion carving — inside the landform pass (its step 5½) when the landforms run, else alone on the generated
  // ground (off: nothing runs, today's planets); like the landforms, it draws nothing from this generation's rng
  // 74.19's yards (DECISIONS 438): the carve is kept aside (`carved`) — everything here and in the setup is placed on the dial
  // off's ground, the same whatever the dial, and the setup's last step lays the carve with their yards held (core/carveYards.ts)
  if (landformsOn) allBodies = allBodies.map(b => b.type === 'planet' ? plannedLandforms(b, erosionDial) : b);
  else if (erosionDial !== 'off') allBodies = allBodies.map(b => b.type === 'planet' ? plannedErosion(b, erosionDial) : b);

  const players = generatePlayers(numPlayers, numBots, allBodies, rng, playerNames, playerLoadouts, botDifficulty, botPersonality, botOverrides, teamIndices, unitsPerTeam, botUnitsPerTeam, numTeams, humanTeams, botTeams, unitSlots, startingCBP, botStartingCBP);

  const hazards = generateHazards(allBodies, players, rng, hazardDensity);
  const ruins = generateRuins(allBodies, players, hazards, rng, ruinDensity, ruinOverrides);
  const flora = generateFlora(allBodies, players, hazards, ruins, rng, 'some');
  const rocks = generateRocks(allBodies, players, hazards, ruins, flora, rng, 'some');
  const environmentalEffects = generateEnvironmentalEffects(allBodies, players, hazards, ruins, flora, rng, 'some');

  return { planets: allBodies, players, hazards, ruins, flora, rocks, environmentalEffects, carryables: [], collectibles: [] };
}

// =============================================================================
// PLANET GENERATION
// =============================================================================

function getRadiusRange(preset: PlanetSizePreset): [number, number] {
  switch (preset) {
    case 'small': return [2, 5];
    case 'large': return [5, 15];
    case 'megaplanet': return [60, 80];
    case 'mixed':
    default: return [3, 10];
  }
}

function generateStars(numStars: number, rng: SeededRandom, starType?: StarType): PlanetState[] {
  const stars: PlanetState[] = [];

  for (let i = 0; i < numStars; i++) {
    // Star-type-driven generation
    const stConfig = starType ? STAR_TYPES[starType] : null;
    const radius = stConfig
      ? rng.range(stConfig.starRadiusRange[0], stConfig.starRadiusRange[1])
      : rng.range(STAR_RADIUS_MIN, STAR_RADIUS_MAX);
    const density = stConfig ? stConfig.starDensity : STAR_DENSITY;
    const mass = density * (4 / 3) * Math.PI * dpow(radius, 3);
    const starColor = stConfig
      ? { core: stConfig.coreColor, corona: stConfig.coronaColor, name: stConfig.name }
      : STAR_COLORS[i % STAR_COLORS.length];

    // Position: 1 star near center, 2 stars offset on opposite sides
    let position: Vec3;
    if (numStars === 1) {
      position = { x: rng.range(-3, 3), y: rng.range(-2, 2), z: rng.range(-3, 3) };
    } else {
      const offset = 12 + rng.range(0, 5);
      const angle = rng.range(0, Math.PI * 2);
      const sign = i === 0 ? 1 : -1;
      position = {
        x: sign * offset * dcos(angle),
        y: rng.range(-3, 3),
        z: sign * offset * dsin(angle),
      };
    }

    // Smooth sphere geometry (no terrain noise)
    const { basePositions, heightmap } = generatePlanetGeometry(radius, rng, 2, true);

    stars.push({
      id: i,
      type: 'star',
      position,
      radius,
      density,
      mass,
      initialMass: mass,
      archetype: 'tephra',
      heightmap,
      basePositions,
      palette: { a: starColor.core, b: starColor.corona, name: starColor.name },
      alive: true,
      indestructible: true,
      ...(starType ? { starType } : {}),
    });
  }

  return stars;
}

function generateWormholes(numPairs: number, rng: SeededRandom, existingBodies: PlanetState[], numPlanets: number = 5, radiusRange: [number, number] = [3, 10]): PlanetState[] {
  const wormholes: PlanetState[] = [];
  let nextId = 1000; // temporary IDs — will be re-indexed in generateMap

  // Scale wormhole distance based on map size
  const avgRadius = (radiusRange[0] + radiusRange[1]) / 2;
  const baseDist = Math.max(25, 15 * Math.sqrt(numPlanets / 5) * (avgRadius / 5.25));
  const distMin = baseDist;
  const distMax = baseDist * 1.4;

  for (let pair = 0; pair < numPairs; pair++) {
    const whColor = WORMHOLE_COLORS[pair % WORMHOLE_COLORS.length];
    const radius = WORMHOLE_RADIUS;
    const density = WORMHOLE_DENSITY;
    const mass = density * (4 / 3) * Math.PI * dpow(radius, 3);

    // Smooth sphere geometry (like stars)
    const geoA = generatePlanetGeometry(radius, rng, 2, true);
    const geoB = generatePlanetGeometry(radius, rng, 2, true);

    // Place wormhole A: random direction, scaled distance from origin
    const distA = rng.range(distMin, distMax);
    const angleA = rng.range(0, Math.PI * 2);
    const yA = rng.range(-5, 5);
    const posA: Vec3 = {
      x: distA * dcos(angleA),
      y: yA,
      z: distA * dsin(angleA),
    };

    // Place wormhole B: opposite quadrant, similar distance
    const distB = rng.range(distMin, distMax);
    const angleB = angleA + Math.PI + rng.range(-0.5, 0.5);
    const yB = rng.range(-5, 5);
    const posB: Vec3 = {
      x: distB * dcos(angleB),
      y: yB,
      z: distB * dsin(angleB),
    };

    const idA = nextId++;
    const idB = nextId++;

    wormholes.push({
      id: idA,
      type: 'wormhole',
      position: posA,
      radius,
      density,
      mass,
      initialMass: mass,
      archetype: 'bruma',
      heightmap: geoA.heightmap,
      basePositions: geoA.basePositions,
      palette: { a: whColor.core, b: whColor.ring, name: whColor.name },
      alive: true,
      indestructible: true,
      linkedWormholeId: idB,
    });

    wormholes.push({
      id: idB,
      type: 'wormhole',
      position: posB,
      radius,
      density,
      mass,
      initialMass: mass,
      archetype: 'bruma',
      heightmap: geoB.heightmap,
      basePositions: geoB.basePositions,
      palette: { a: whColor.core, b: whColor.ring, name: whColor.name },
      alive: true,
      indestructible: true,
      linkedWormholeId: idA,
    });
  }

  return wormholes;
}

function generatePlanets(
  numPlanets: number,
  rng: SeededRandom,
  existingBodies: PlanetState[] = [],
  planetSize: PlanetSizePreset = 'mixed',
  layout: MapLayout = 'random',
  starType?: StarType
): PlanetState[] {
  const planets: PlanetState[] = [];
  const radiusRange = getRadiusRange(planetSize);
  const avgRadius = (radiusRange[0] + radiusRange[1]) / 2;

  // Check if stars are present — if so, use orbital placement (layout ignored)
  const stars = existingBodies.filter(b => b.type === 'star');

  if (stars.length > 0) {
    const planetsPerStar = Math.ceil(numPlanets / stars.length);
    let planetIdx = 0;

    for (let si = 0; si < stars.length && planetIdx < numPlanets; si++) {
      const star = stars[si];
      const innerOrbit = star.radius * STAR_MIN_PLANET_DISTANCE_MULT;
      const count = Math.min(planetsPerStar, numPlanets - planetIdx);

      for (let j = 0; j < count; j++) {
        let tries = 0;
        let placed = false;

        while (tries < 200 && !placed) {
          const radius = starType
            ? pickStarDrivenRadius(starType, rng)
            : rng.range(radiusRange[0], radiusRange[1]);
          const density = rng.range(0.3, 1.2);

          // Scale ring spacing based on average radius
          const orbitDist = innerOrbit + 8 + j * rng.range(avgRadius * 1.5, avgRadius * 2.5);
          const angle = rng.range(0, Math.PI * 2);
          const yTilt = rng.range(-4, 4);
          const position: Vec3 = {
            x: star.position.x + orbitDist * dcos(angle),
            y: star.position.y + yTilt,
            z: star.position.z + orbitDist * dsin(angle),
          };

          if (checkSpacing(position, radius, [...existingBodies, ...planets])) {
            if (starType) {
              const zone = getOrbitalZone(planetIdx, numPlanets);
              const archetype = pickStarDrivenBiome(starType, zone, rng);
              const palette = PLANET_PALETTES.find(p => p.name === archetype) ?? ARCHETYPE_PALETTES[planetIdx % ARCHETYPE_PALETTES.length];
              const planet = createPlanetState(planetIdx, position, radius, density, palette, rng, archetype);
              planets.push(planet);
            } else {
              const palette = ARCHETYPE_PALETTES[planetIdx % ARCHETYPE_PALETTES.length];
              const planet = createPlanetState(planetIdx, position, radius, density, palette, rng);
              planets.push(planet);
            }
            placed = true;
          }

          tries++;
        }
        planetIdx++;
      }
    }
    return planets;
  }

  // No stars: dispatch to layout algorithm
  switch (layout) {
    case 'linear':
      return generateLinearLayout(numPlanets, rng, existingBodies, radiusRange, avgRadius, starType);
    case 'ring':
      return generateRingLayout(numPlanets, rng, existingBodies, radiusRange, avgRadius, starType);
    case 'layered':
      return generateLayeredLayout(numPlanets, rng, existingBodies, radiusRange, avgRadius, starType);
    case 'spiral':
      return generateSpiralLayout(numPlanets, rng, existingBodies, radiusRange, avgRadius, starType);
    case 'clusters':
      return generateClustersLayout(numPlanets, rng, existingBodies, radiusRange, avgRadius, starType);
    case 'scatter':
      return generateScatterLayout(numPlanets, rng, existingBodies, radiusRange, avgRadius, starType);
    case 'random':
    default:
      return generateRandomLayout(numPlanets, rng, existingBodies, radiusRange, avgRadius, starType);
  }
}

/** Maximum distance from origin for any planet center (must be well within OUT_OF_BOUNDS=500) */
const MAX_PLANET_DISTANCE = 200;

/** Check spacing from all existing bodies, min spacing = (r1+r2)*2.5, star distance uses multiplier */
function checkSpacing(position: Vec3, radius: number, existingBodies: PlanetState[]): boolean {
  for (const p of existingBodies) {
    const dist = vecLength(vecSub(position, p.position));
    const minDist = p.type === 'star'
      ? p.radius * STAR_MIN_PLANET_DISTANCE_MULT + radius
      : (radius + p.radius) * 2.5;
    if (dist < minDist) return false;
  }
  return true;
}

/** Place a planet with retry logic, returns true if placed */
function tryPlacePlanet(
  position: Vec3,
  radius: number,
  density: number,
  index: number,
  rng: SeededRandom,
  planets: PlanetState[],
  existingBodies: PlanetState[],
  starType?: StarType,
  totalPlanets?: number
): boolean {
  // Reject positions too far from origin — prevents planets outside the playable area
  if (vecLength(position) > MAX_PLANET_DISTANCE) return false;

  if (checkSpacing(position, radius, [...existingBodies, ...planets])) {
    if (starType && totalPlanets) {
      const zone = getOrbitalZone(index, totalPlanets);
      const archetype = pickStarDrivenBiome(starType, zone, rng);
      const palette = PLANET_PALETTES.find(p => p.name === archetype) ?? ARCHETYPE_PALETTES[index % ARCHETYPE_PALETTES.length];
      const planet = createPlanetState(index, position, radius, density, palette, rng, archetype);
      planets.push(planet);
    } else {
      const palette = ARCHETYPE_PALETTES[index % ARCHETYPE_PALETTES.length];
      const planet = createPlanetState(index, position, radius, density, palette, rng);
      planets.push(planet);
    }
    return true;
  }
  return false;
}

/** Random layout — original algorithm with dynamically scaled bounds */
function generateRandomLayout(
  numPlanets: number,
  rng: SeededRandom,
  existingBodies: PlanetState[],
  radiusRange: [number, number],
  avgRadius: number,
  starType?: StarType
): PlanetState[] {
  const planets: PlanetState[] = [];
  const scaleFactor = Math.sqrt(numPlanets / 5) * (avgRadius / 5.25);
  const boundsX = Math.min(30 * scaleFactor, 200);
  const boundsY = Math.min(18 * scaleFactor, 200);
  const boundsZ = Math.min(25 * scaleFactor, 200);

  for (let i = 0; i < numPlanets; i++) {
    let tries = 0;
    let placed = false;

    while (tries < 200 && !placed) {
      const radius = starType ? pickStarDrivenRadius(starType, rng) : rng.range(radiusRange[0], radiusRange[1]);
      const density = rng.range(0.3, 1.2);
      const position: Vec3 = {
        x: rng.range(-boundsX, boundsX),
        y: rng.range(-boundsY, boundsY),
        z: rng.range(-boundsZ, boundsZ),
      };

      placed = tryPlacePlanet(position, radius, density, i, rng, planets, existingBodies, starType, numPlanets);
      tries++;
    }
  }

  return planets;
}

/** Linear layout — planets along a gently curving line */
function generateLinearLayout(
  numPlanets: number,
  rng: SeededRandom,
  existingBodies: PlanetState[],
  radiusRange: [number, number],
  avgRadius: number,
  starType?: StarType
): PlanetState[] {
  const planets: PlanetState[] = [];
  const rawSpacing = avgRadius * 3.5;
  const rawLength = (numPlanets - 1) * rawSpacing;
  // Cap total length so endpoints stay within MAX_PLANET_DISTANCE
  const maxLength = MAX_PLANET_DISTANCE * 1.8; // half-length = 0.9 * MAX
  const totalLength = Math.min(rawLength, maxLength);
  const spacing = numPlanets > 1 ? totalLength / (numPlanets - 1) : rawSpacing;
  const startX = -totalLength / 2;
  const amplitude = Math.min(totalLength * 0.1, 20);

  for (let i = 0; i < numPlanets; i++) {
    let tries = 0;
    let placed = false;

    while (tries < 200 && !placed) {
      const radius = starType ? pickStarDrivenRadius(starType, rng) : rng.range(radiusRange[0], radiusRange[1]);
      const density = rng.range(0.3, 1.2);
      const t = numPlanets > 1 ? i / (numPlanets - 1) : 0.5;
      const baseX = startX + i * spacing;
      const position: Vec3 = {
        x: baseX + rng.range(-avgRadius * 0.3, avgRadius * 0.3),
        y: dsin(t * Math.PI * 2) * amplitude + rng.range(-avgRadius * 0.3, avgRadius * 0.3),
        z: dcos(t * Math.PI * 1.5) * amplitude * 0.7 + rng.range(-avgRadius * 0.3, avgRadius * 0.3),
      };

      placed = tryPlacePlanet(position, radius, density, i, rng, planets, existingBodies, starType, numPlanets);
      tries++;
    }
  }

  return planets;
}

/** Ring layout — planets in a circle */
function generateRingLayout(
  numPlanets: number,
  rng: SeededRandom,
  existingBodies: PlanetState[],
  radiusRange: [number, number],
  avgRadius: number,
  starType?: StarType
): PlanetState[] {
  const planets: PlanetState[] = [];
  const minSpacing = (radiusRange[0] + radiusRange[1]) * 2.5;
  const ringRadius = Math.max(numPlanets * minSpacing / (2 * Math.PI), avgRadius * 4);

  for (let i = 0; i < numPlanets; i++) {
    let tries = 0;
    let placed = false;

    while (tries < 200 && !placed) {
      const radius = starType ? pickStarDrivenRadius(starType, rng) : rng.range(radiusRange[0], radiusRange[1]);
      const density = rng.range(0.3, 1.2);
      const baseAngle = (i / numPlanets) * Math.PI * 2;
      const angle = baseAngle + rng.range(-0.15, 0.15);
      const radialJitter = rng.range(-avgRadius * 0.4, avgRadius * 0.4);
      const position: Vec3 = {
        x: (ringRadius + radialJitter) * dcos(angle),
        y: rng.range(-avgRadius * 0.5, avgRadius * 0.5),
        z: (ringRadius + radialJitter) * dsin(angle),
      };

      placed = tryPlacePlanet(position, radius, density, i, rng, planets, existingBodies, starType, numPlanets);
      tries++;
    }
  }

  return planets;
}

/** Layered layout — 3 horizontal planes */
function generateLayeredLayout(
  numPlanets: number,
  rng: SeededRandom,
  existingBodies: PlanetState[],
  radiusRange: [number, number],
  avgRadius: number,
  starType?: StarType
): PlanetState[] {
  const planets: PlanetState[] = [];
  const layerSpacing = avgRadius * 4 + 5;
  const layerYs = [-layerSpacing, 0, layerSpacing];

  // Distribute planets across 3 layers: middle gets extra (remainder)
  const perLayer = Math.floor(numPlanets / 3);
  const remainder = numPlanets % 3;
  const layerCounts = [perLayer, perLayer + remainder, perLayer];

  // Scale XZ bounds per layer
  const scaleFactor = Math.sqrt(numPlanets / 5) * (avgRadius / 5.25);
  const boundsX = Math.min(30 * scaleFactor, 200);
  const boundsZ = Math.min(25 * scaleFactor, 200);

  let planetIdx = 0;
  for (let layer = 0; layer < 3; layer++) {
    const baseY = layerYs[layer];
    for (let j = 0; j < layerCounts[layer]; j++) {
      let tries = 0;
      let placed = false;

      while (tries < 200 && !placed) {
        const radius = starType ? pickStarDrivenRadius(starType, rng) : rng.range(radiusRange[0], radiusRange[1]);
        const density = rng.range(0.3, 1.2);
        const position: Vec3 = {
          x: rng.range(-boundsX, boundsX),
          y: baseY + rng.range(-avgRadius * 0.3, avgRadius * 0.3),
          z: rng.range(-boundsZ, boundsZ),
        };

        placed = tryPlacePlanet(position, radius, density, planetIdx, rng, planets, existingBodies, starType, numPlanets);
        tries++;
      }
      planetIdx++;
    }
  }

  return planets;
}

/** Spiral layout — Archimedean spiral on XZ plane with golden-angle stepping */
function generateSpiralLayout(
  numPlanets: number,
  rng: SeededRandom,
  existingBodies: PlanetState[],
  radiusRange: [number, number],
  avgRadius: number,
  starType?: StarType
): PlanetState[] {
  const planets: PlanetState[] = [];
  const goldenAngle = 2.3998632; // ~137.5 degrees in radians
  // Scale arm spacing so outermost planet stays within MAX_PLANET_DISTANCE
  const maxArmSpacing = avgRadius * 3.0;
  const maxOuterDist = avgRadius * 3 + (numPlanets - 1) * maxArmSpacing;
  const spiralScale = maxOuterDist > MAX_PLANET_DISTANCE * 0.9
    ? (MAX_PLANET_DISTANCE * 0.9) / maxOuterDist : 1.0;
  const armSpacing = maxArmSpacing * spiralScale;
  const baseRadius = avgRadius * 3 * spiralScale;

  for (let i = 0; i < numPlanets; i++) {
    let tries = 0;
    let placed = false;

    while (tries < 200 && !placed) {
      const radius = starType ? pickStarDrivenRadius(starType, rng) : rng.range(radiusRange[0], radiusRange[1]);
      const density = rng.range(0.3, 1.2);
      const angle = i * goldenAngle;
      const dist = baseRadius + i * armSpacing + rng.range(-avgRadius * 0.3, avgRadius * 0.3);
      const position: Vec3 = {
        x: dist * dcos(angle),
        y: rng.range(-avgRadius * 0.5, avgRadius * 0.5),
        z: dist * dsin(angle),
      };

      placed = tryPlacePlanet(position, radius, density, i, rng, planets, existingBodies, starType, numPlanets);
      tries++;
    }
  }

  return planets;
}

/** Clusters layout — 2-4 tight groups with wide gaps between them */
function generateClustersLayout(
  numPlanets: number,
  rng: SeededRandom,
  existingBodies: PlanetState[],
  radiusRange: [number, number],
  avgRadius: number,
  starType?: StarType
): PlanetState[] {
  const planets: PlanetState[] = [];
  const scaleFactor = Math.sqrt(numPlanets / 5) * (avgRadius / 5.25);
  const boundsX = Math.min(30 * scaleFactor, 200);
  const numClusters = Math.max(2, Math.min(4, Math.floor(numPlanets / 3)));
  const clusterRadius = avgRadius * 3;

  // Generate cluster centers on a ring
  const ringRadius = boundsX * 0.6;
  const clusterCenters: Vec3[] = [];
  for (let c = 0; c < numClusters; c++) {
    const baseAngle = (c / numClusters) * Math.PI * 2;
    const angle = baseAngle + rng.range(-0.3, 0.3);
    clusterCenters.push({
      x: ringRadius * dcos(angle),
      y: rng.range(-avgRadius * 0.5, avgRadius * 0.5),
      z: ringRadius * dsin(angle),
    });
  }

  // Distribute planets across clusters
  const perCluster = Math.floor(numPlanets / numClusters);
  const remainder = numPlanets % numClusters;

  let planetIdx = 0;
  for (let c = 0; c < numClusters; c++) {
    const count = perCluster + (c < remainder ? 1 : 0);
    const center = clusterCenters[c];

    for (let j = 0; j < count; j++) {
      let tries = 0;
      let placed = false;

      while (tries < 200 && !placed) {
        const radius = starType ? pickStarDrivenRadius(starType, rng) : rng.range(radiusRange[0], radiusRange[1]);
        const density = rng.range(0.3, 1.2);
        const position: Vec3 = {
          x: center.x + rng.range(-clusterRadius, clusterRadius),
          y: center.y + rng.range(-clusterRadius * 0.5, clusterRadius * 0.5),
          z: center.z + rng.range(-clusterRadius, clusterRadius),
        };

        placed = tryPlacePlanet(position, radius, density, planetIdx, rng, planets, existingBodies, starType, numPlanets);
        tries++;
      }
      planetIdx++;
    }
  }

  return planets;
}

/** Scatter layout — wild 3D spread with 1.5× bigger bounds and equal Y spread */
function generateScatterLayout(
  numPlanets: number,
  rng: SeededRandom,
  existingBodies: PlanetState[],
  radiusRange: [number, number],
  avgRadius: number,
  starType?: StarType
): PlanetState[] {
  const planets: PlanetState[] = [];
  const scaleFactor = Math.sqrt(numPlanets / 5) * (avgRadius / 5.25) * 1.5;
  // Cap bounds so corner positions stay within MAX_PLANET_DISTANCE (~115 per axis for 200 diagonal)
  const maxAxisBound = MAX_PLANET_DISTANCE * 0.58;
  const boundsX = Math.min(30 * scaleFactor, maxAxisBound);
  const boundsZ = Math.min(25 * scaleFactor, maxAxisBound);
  // Y bounds equal to X bounds for truly 3D chaotic spread
  const boundsY = boundsX;

  for (let i = 0; i < numPlanets; i++) {
    let tries = 0;
    let placed = false;

    while (tries < 200 && !placed) {
      const radius = starType ? pickStarDrivenRadius(starType, rng) : rng.range(radiusRange[0], radiusRange[1]);
      const density = rng.range(0.3, 1.2);
      const position: Vec3 = {
        x: rng.range(-boundsX, boundsX),
        y: rng.range(-boundsY, boundsY),
        z: rng.range(-boundsZ, boundsZ),
      };

      placed = tryPlacePlanet(position, radius, density, i, rng, planets, existingBodies, starType, numPlanets);
      tries++;
    }
  }

  return planets;
}

// =============================================================================
// STAR-DRIVEN BIOME / SIZE HELPERS
// =============================================================================

export function getOrbitalZone(planetIndex: number, totalPlanets: number): OrbitalZone {
  if (totalPlanets <= 1) return 'middle';
  const t = planetIndex / (totalPlanets - 1);
  if (t < 0.25) return 'inner';
  if (t > 0.75) return 'outer';
  return 'middle';
}

export function pickStarDrivenBiome(starType: StarType, zone: OrbitalZone, rng: SeededRandom): PlanetArchetype {
  const affinities = BIOME_AFFINITY[starType];
  const modifiers = ORBITAL_BIOME_MODIFIERS[zone];
  const archetypes = Object.keys(affinities) as PlanetArchetype[];

  let totalWeight = 0;
  const weights: number[] = [];
  for (const arch of archetypes) {
    const w = affinities[arch] * modifiers[arch];
    weights.push(w);
    totalWeight += w;
  }

  if (totalWeight <= 0) {
    // Fallback: return middle-of-the-road archetype
    return 'pallor';
  }

  let roll = rng.range(0, totalWeight);
  for (let i = 0; i < archetypes.length; i++) {
    roll -= weights[i];
    if (roll <= 0) return archetypes[i];
  }
  return archetypes[archetypes.length - 1];
}

export function pickStarDrivenRadius(starType: StarType, rng: SeededRandom): number {
  const sizeWeights = PLANET_SIZE_WEIGHTS[starType];
  const categories = Object.keys(sizeWeights) as PlanetSizeCategory[];

  let totalWeight = 0;
  for (const cat of categories) totalWeight += sizeWeights[cat];

  let roll = rng.range(0, totalWeight);
  let selectedCat: PlanetSizeCategory = 'medium';
  for (const cat of categories) {
    roll -= sizeWeights[cat];
    if (roll <= 0) {
      selectedCat = cat;
      break;
    }
  }

  const [minR, maxR] = SIZE_CATEGORY_RADIUS[selectedCat];
  return rng.range(minR, maxR);
}

function ensureBiomeDiversity(bodies: PlanetState[], starType: StarType, rng: SeededRandom): PlanetState[] {
  const planetIndices = bodies.map((b, i) => ({ body: b, idx: i })).filter(e => e.body.type === 'planet');
  if (planetIndices.length < 4) return bodies; // too few to enforce diversity

  // Count biomes
  const biomeCounts = new Map<PlanetArchetype, number[]>(); // archetype → list of body indices
  for (const { body, idx } of planetIndices) {
    const list = biomeCounts.get(body.archetype) ?? [];
    list.push(idx);
    biomeCounts.set(body.archetype, list);
  }

  const result = [...bodies];

  // Guarantee 3+ distinct biomes if 4+ planets
  const distinctBiomes = biomeCounts.size;
  if (distinctBiomes < 3) {
    // Find under-represented biomes (weight > 0 in affinity)
    const affinities = BIOME_AFFINITY[starType];
    const eligibleBiomes = (Object.keys(affinities) as PlanetArchetype[]).filter(
      a => affinities[a] > 0 && !biomeCounts.has(a)
    );

    // Find the most-common biome to steal from
    let mostCommon: PlanetArchetype | null = null;
    let maxCount = 0;
    for (const [arch, indices] of biomeCounts) {
      if (indices.length > maxCount) {
        maxCount = indices.length;
        mostCommon = arch;
      }
    }

    // Re-roll excess planets from the most-common biome
    const needed = 3 - distinctBiomes;
    for (let n = 0; n < needed && eligibleBiomes.length > 0 && mostCommon; n++) {
      const mcIndices = biomeCounts.get(mostCommon)!;
      if (mcIndices.length <= 1) break; // don't deplete completely

      const victimIdx = mcIndices.pop()!;
      const newArchetype = eligibleBiomes.shift()!;
      const newPalette = PLANET_PALETTES.find(p => p.name === newArchetype) ?? result[victimIdx].palette;
      const archetypeConfig = PLANET_ARCHETYPES[newArchetype];
      const newDensity = rng.range(archetypeConfig.densityRange[0], archetypeConfig.densityRange[1]);
      const newMass = newDensity * (4 / 3) * Math.PI * dpow(result[victimIdx].radius, 3);

      result[victimIdx] = {
        ...result[victimIdx],
        archetype: newArchetype,
        palette: newPalette,
        density: newDensity,
        mass: newMass,
        initialMass: newMass,
      };

      biomeCounts.set(mostCommon, mcIndices);
      biomeCounts.set(newArchetype, [victimIdx]);
    }
  }

  // Cap any single biome at 3 occurrences if <12 planets
  if (planetIndices.length < 12) {
    // Re-count after diversity pass
    const recount = new Map<PlanetArchetype, number[]>();
    for (const { body, idx } of planetIndices) {
      const arch = result[idx].archetype;
      const list = recount.get(arch) ?? [];
      list.push(idx);
      recount.set(arch, list);
    }

    const affinities = BIOME_AFFINITY[starType];
    for (const [arch, indices] of recount) {
      while (indices.length > 3) {
        const victimIdx = indices.pop()!;
        // Pick a random under-represented biome
        const underRep = (Object.keys(affinities) as PlanetArchetype[]).filter(
          a => affinities[a] > 0 && (recount.get(a)?.length ?? 0) < 2
        );
        if (underRep.length === 0) break;
        const newArchetype = underRep[rng.int(0, underRep.length - 1)];
        const newPalette = PLANET_PALETTES.find(p => p.name === newArchetype) ?? result[victimIdx].palette;
        const archetypeConfig = PLANET_ARCHETYPES[newArchetype];
        const newDensity = rng.range(archetypeConfig.densityRange[0], archetypeConfig.densityRange[1]);
        const newMass = newDensity * (4 / 3) * Math.PI * dpow(result[victimIdx].radius, 3);

        result[victimIdx] = {
          ...result[victimIdx],
          archetype: newArchetype,
          palette: newPalette,
          density: newDensity,
          mass: newMass,
          initialMass: newMass,
        };

        const newList = recount.get(newArchetype) ?? [];
        newList.push(victimIdx);
        recount.set(newArchetype, newList);
      }
    }
  }

  return result;
}

function createPlanetState(
  id: number,
  position: Vec3,
  radius: number,
  density: number,
  palette: { a: string; b: string; name: string },
  rng: SeededRandom,
  archetypeOverride?: PlanetArchetype
): PlanetState {
  // Determine archetype BEFORE geometry generation so terrain profile can be applied
  archetypeOverride = forcedArchetype ?? archetypeOverride;   // 67.41: the Lab's Planet row wins
  let archetype: PlanetArchetype;
  let visualPalette: { a: string; b: string; name: string };

  if (archetypeOverride) {
    // 67.41's follow-up: a forced kind (the Lab's row, a seed's draw) still takes the star pick's draw, so the rest of the
    // planet comes from the seed as before — thalia forced on a seed that drew thalia is that same world
    if (forcedArchetype) rng.range(0, 1);
    archetype = archetypeOverride;
    visualPalette = PLANET_PALETTES.find(p => p.name === archetype) ?? palette;
  } else {
    const isSonora = rng.range(0, 1) < 0.1;
    archetype = isSonora ? 'sonora' : palette.name as PlanetArchetype;
    visualPalette = isSonora
      ? PLANET_PALETTES.find(p => p.name === 'sonora')!
      : palette;
  }

  // Use archetype-specific density range
  const archetypeConfig = PLANET_ARCHETYPES[archetype];
  const archetypeDensity = rng.range(archetypeConfig.densityRange[0], archetypeConfig.densityRange[1]);
  const archetypeMass = archetypeDensity * (4 / 3) * Math.PI * dpow(radius, 3);

  // Create icosahedron geometry with archetype-specific terrain profile
  const profile = TERRAIN_PROFILES[archetype];
  // (74.15: the finer mesh samples the same world finer — the heights are functions of the direction and the rng's draws
  // per layer, not per vertex — so every later draw, and so the map, is the same)
  const detail = planetDetailForRadius(radius) + (finerMeshOn ? 1 : 0);
  const { basePositions, heightmap } = generatePlanetGeometry(radius, rng, detail, false, profile);

  return {
    id,
    type: 'planet',
    position,
    radius,
    density: archetypeDensity,
    mass: archetypeMass,
    initialMass: archetypeMass,
    archetype,
    heightmap,
    initialHeightmap: heightmap,   // never mutated (craters copy); the material bands are fixed against it
    basePositions,
    palette: visualPalette,
    alive: true,
    indestructible: false,
  };
}

/**
 * Deterministic per-position jitter in [-1, 1). Hashing the (quantized) unit
 * direction instead of drawing from the RNG per vertex means the 5-6 coincident
 * copies of every shared corner in the non-indexed icosphere get the SAME
 * height, so the mesh stays watertight (no see-through seams, BUG-002).
 */
export function positionJitter(nx: number, ny: number, nz: number): number {
  let h = (Math.round(nx * 1e5) | 0) * 374761393;
  h = (h ^ ((Math.round(ny * 1e5) | 0) * 668265263)) | 0;
  h = (h ^ ((Math.round(nz * 1e5) | 0) * 2147483647)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 2147483648 - 1; // [0, 2^32) -> [-1, 1)
}

const DEFAULT_RELIEF_RANGE: [number, number] = [0.7, 1.5];
const DEFAULT_REGIONAL_RELIEF = 0.4;

/**
 * Icosphere subdivision by planet radius: detail 3 (3,840 verts), 4 (15,360), 5 (61,440).
 * One detail for every planet left r 3 with 0.2-unit triangle edges and r 16 with
 * 1.2-unit edges — a 1.3-radius blast carved six triangles on the small planet and
 * barely one on the large. This keeps edges roughly 0.4–0.8 units. Deterministic
 * from the radius, so every peer builds the same mesh. Measured cost (Oct 2026):
 * +15 % vertices on a mixed map; per-impact recolour is blast-local so detail 5
 * does not stall.
 */
export function planetDetailForRadius(radius: number): number {
  return radius < 4 ? 3 : radius < 12 ? 4 : 5;
}

export function generatePlanetGeometry(
  radius: number,
  rng: SeededRandom,
  detail: number = 4,
  smooth: boolean = false,
  profile?: TerrainProfile
): { basePositions: Float32Array; heightmap: Float32Array } {
  // Generate icosahedron with subdivision - must match Three.js IcosahedronGeometry(radius, detail)
  // Three.js uses non-indexed geometry, so we need to generate all triangle vertices

  // Golden ratio for icosahedron
  const t = (1 + Math.sqrt(5)) / 2;

  // 12 base vertices of icosahedron (normalized)
  const baseVerts: number[] = [];
  const rawVerts = [
    [-1, t, 0], [1, t, 0], [-1, -t, 0], [1, -t, 0],
    [0, -1, t], [0, 1, t], [0, -1, -t], [0, 1, -t],
    [t, 0, -1], [t, 0, 1], [-t, 0, -1], [-t, 0, 1],
  ];
  for (const v of rawVerts) {
    const len = Math.sqrt((v[0] * v[0]) + (v[1] * v[1]) + (v[2] * v[2]));
    baseVerts.push(v[0] / len, v[1] / len, v[2] / len);
  }

  // 20 faces of icosahedron (vertex indices)
  const baseFaces = [
    [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
    [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
    [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
    [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1],
  ];

  // Subdivide each face
  const finalVertices: number[] = [];

  const getVertex = (idx: number): [number, number, number] => {
    return [baseVerts[idx * 3], baseVerts[idx * 3 + 1], baseVerts[idx * 3 + 2]];
  };

  const normalizeAndPush = (x: number, y: number, z: number) => {
    const len = Math.sqrt(x * x + y * y + z * z);
    finalVertices.push(x / len, y / len, z / len);
  };

  const subdivideFace = (
    v1: [number, number, number],
    v2: [number, number, number],
    v3: [number, number, number],
    depth: number
  ) => {
    if (depth === 0) {
      // Add triangle vertices
      normalizeAndPush(v1[0], v1[1], v1[2]);
      normalizeAndPush(v2[0], v2[1], v2[2]);
      normalizeAndPush(v3[0], v3[1], v3[2]);
      return;
    }

    // Midpoints
    const m12: [number, number, number] = [
      (v1[0] + v2[0]) / 2,
      (v1[1] + v2[1]) / 2,
      (v1[2] + v2[2]) / 2,
    ];
    const m23: [number, number, number] = [
      (v2[0] + v3[0]) / 2,
      (v2[1] + v3[1]) / 2,
      (v2[2] + v3[2]) / 2,
    ];
    const m31: [number, number, number] = [
      (v3[0] + v1[0]) / 2,
      (v3[1] + v1[1]) / 2,
      (v3[2] + v1[2]) / 2,
    ];

    // Normalize midpoints to sphere
    const normalize = (v: [number, number, number]): [number, number, number] => {
      const len = Math.sqrt((v[0] * v[0]) + (v[1] * v[1]) + (v[2] * v[2]));
      return [v[0] / len, v[1] / len, v[2] / len];
    };

    const nm12 = normalize(m12);
    const nm23 = normalize(m23);
    const nm31 = normalize(m31);

    // Recurse on 4 sub-triangles
    subdivideFace(v1, nm12, nm31, depth - 1);
    subdivideFace(nm12, v2, nm23, depth - 1);
    subdivideFace(nm31, nm23, v3, depth - 1);
    subdivideFace(nm12, nm23, nm31, depth - 1);
  };

  for (const face of baseFaces) {
    subdivideFace(getVertex(face[0]), getVertex(face[1]), getVertex(face[2]), detail);
  }

  const vertexCount = finalVertices.length / 3;
  const basePositions = new Float32Array(finalVertices.length);
  for (let i = 0; i < finalVertices.length; i++) {
    basePositions[i] = finalVertices[i] * radius;
  }

  const heightmap = new Float32Array(vertexCount);
  if (!smooth) {
    if (profile) {
      // Generate per-octave seed offsets for variation between planets
      const octaveOffsets: { ox: number; oy: number; oz: number }[] = [];
      for (let o = 0; o < profile.octaves.length; o++) {
        octaveOffsets.push({
          ox: rng.range(-100, 100),
          oy: rng.range(-100, 100),
          oz: rng.range(-100, 100),
        });
      }

      // Relief variation (no extra rng draws, so existing seeds keep their layouts):
      // a per-planet scale hashed from the first octave's offsets — some worlds are
      // flat, some rugged — and a low-frequency regional mask so plains and badlands
      // coexist on one planet.
      const [reliefMin, reliefMax] = profile.reliefRange ?? DEFAULT_RELIEF_RANGE;
      const regional = profile.regionalRelief ?? DEFAULT_REGIONAL_RELIEF;
      const o0 = octaveOffsets[0];
      // 74.22 stage 1: the lattice jitter's wavelength, in radians, from the planet's generated detail (not the finer mesh's: the same world either way)
      const jitterWavelength = GROUND_JITTER.facets * facetAngleOf(planetDetailForRadius(radius));
      const reliefScale = reliefMin + (reliefMax - reliefMin) * (positionJitter(o0.ox * 0.013, o0.oy * 0.017, o0.oz * 0.011) + 1) / 2;
      const maxUp = radius * MAX_TERRAIN_HEIGHT_FRACTION;
      const maxDown = -radius * MAX_CRATER_DEPTH_FRACTION;

      for (let i = 0; i < vertexCount; i++) {
        const nx = finalVertices[i * 3];
        const ny = finalVertices[i * 3 + 1];
        const nz = finalVertices[i * 3 + 2];

        // Accumulate octaves
        let h = 0;
        for (let o = 0; o < profile.octaves.length; o++) {
          const { freq, amp } = profile.octaves[o];
          const { ox, oy, oz } = octaveOffsets[o];
          h += dsin((nx + ox) * freq + (ny + oy) * (freq * 0.7)) * amp;
          h += dsin((nz + oz) * freq * 1.3 + (nx + ox) * (freq * 0.4)) * amp * 0.5;
        }

        // Ridge transform: creates sharp peaks from smooth sine
        if (profile.ridgeFraction > 0) {
          const ridged = 1 - Math.abs(h);
          h = h * (1 - profile.ridgeFraction) + ridged * profile.ridgeFraction;
        }

        // Plateau transform: flattens peaks
        if (profile.plateauFraction > 0) {
          const cap = 0.6;
          const clamped = Math.max(-cap, Math.min(cap, h));
          h = h * (1 - profile.plateauFraction) + clamped * profile.plateauFraction;
        }

        // Per-vertex jitter — hashed from position so shared corners agree (no seams); 74.22 stage 1: or the band-limited
        // lattice field at a few facets' wavelength (core/groundNoise.ts), behind the action setup's flag
        h += (groundJitterMode === 'lattice' ? latticeJitter(nx, ny, nz, jitterWavelength, o0.ox, o0.oy, o0.oz) : positionJitter(nx, ny, nz)) * profile.jitter;

        // Regional relief: smooth mask in [1 - regional, 1 + regional]
        const mask = 1 + regional * dsin((nx + o0.ox) * 1.4 + (ny + o0.oy) * 1.1) * dcos((nz + o0.oz) * 1.2 + (ny + o0.oy) * 0.6);
        h *= reliefScale * mask;

        heightmap[i] = Math.max(maxDown, Math.min(maxUp, h * radius * profile.amplitude));
      }

      // Mountain pass — only on large planets (radius >= 8)
      if (radius >= 8) {
        const numPeaks = rng.int(2, 5);
        const peaks: { dx: number; dy: number; dz: number; height: number; spread: number }[] = [];
        for (let p = 0; p < numPeaks; p++) {
          // Random direction on unit sphere via uniform sampling
          const theta = rng.range(0, Math.PI * 2);
          const cosPhi = rng.range(-1, 1);
          const sinPhi = Math.sqrt(1 - cosPhi * cosPhi);
          peaks.push({
            dx: sinPhi * dcos(theta),
            dy: sinPhi * dsin(theta),
            dz: cosPhi,
            height: rng.range(0.06, 0.14) * radius,  // subtle — 6-14% of radius
            spread: rng.range(0.15, 0.35),             // angular spread in radians
          });
        }

        for (let i = 0; i < vertexCount; i++) {
          const nx = finalVertices[i * 3];
          const ny = finalVertices[i * 3 + 1];
          const nz = finalVertices[i * 3 + 2];
          for (const peak of peaks) {
            const dot = nx * peak.dx + ny * peak.dy + nz * peak.dz;
            const angle = dacos(Math.min(1, Math.max(-1, dot)));
            if (angle < peak.spread * 3) {
              // Gaussian-ish falloff
              const t = angle / peak.spread;
              heightmap[i] += peak.height * dexp(-t * t * 0.5);
            }
          }
        }
      }
    } else {
      // Legacy fallback: original 3-sine noise
      for (let i = 0; i < vertexCount; i++) {
        const nx = finalVertices[i * 3];
        const ny = finalVertices[i * 3 + 1];
        const nz = finalVertices[i * 3 + 2];

        let h = 0;
        h += dsin(nx * 4.3 + ny * 3.1) * 0.35;
        h += dsin(nz * 6.7 + nx * 1.9) * 0.25;
        h += dsin(ny * 9.3 + nz * 5.1 + nx * 2.7) * 0.15;
        h += positionJitter(nx, ny, nz) * 0.08;

        heightmap[i] = h * radius * 0.1;
      }
    }
  }

  // Apply terrain stamps after base noise + mountain peaks
  if (profile?.stamps && profile.stamps.length > 0) {
    const placed = placeStamps(profile.stamps, rng, radius);
    applyStamps(placed, finalVertices, heightmap, vertexCount, radius);
  }

  return { basePositions, heightmap };
}

// =============================================================================
// EARTH HEIGHTMAP — deterministic continent placement
// =============================================================================

/** Spherical cap definition: center in lat/lon (degrees) + angular radius (radians) */
interface LandCap {
  lat: number;  // degrees, -90..90
  lon: number;  // degrees, -180..180
  r: number;    // angular radius in radians
}

/** Mountain range cap with higher amplitude */
interface MountainCap extends LandCap {
  peak: number; // extra height multiplier (0..1)
}

// ~30 overlapping caps approximating Earth's major landmasses
const EARTH_LAND_CAPS: LandCap[] = [
  // North America
  { lat: 55, lon: -100, r: 0.45 },
  { lat: 40, lon: -95, r: 0.30 },
  { lat: 30, lon: -105, r: 0.20 },
  { lat: 65, lon: -135, r: 0.25 }, // Alaska
  { lat: 20, lon: -100, r: 0.12 }, // Mexico
  // South America
  { lat: -10, lon: -60, r: 0.35 },
  { lat: -25, lon: -55, r: 0.25 },
  { lat: -40, lon: -68, r: 0.15 },
  { lat: 5, lon: -72, r: 0.15 },   // Colombia/Venezuela
  // Europe
  { lat: 50, lon: 10, r: 0.25 },
  { lat: 55, lon: 25, r: 0.20 },
  { lat: 40, lon: 0, r: 0.15 },    // Iberia
  { lat: 60, lon: 10, r: 0.12 },   // Scandinavia
  // Africa
  { lat: 10, lon: 20, r: 0.40 },
  { lat: -10, lon: 25, r: 0.30 },
  { lat: -25, lon: 28, r: 0.18 },
  { lat: 30, lon: 5, r: 0.18 },    // North Africa
  // Asia
  { lat: 50, lon: 80, r: 0.50 },
  { lat: 35, lon: 105, r: 0.30 },
  { lat: 25, lon: 80, r: 0.25 },   // India
  { lat: 60, lon: 100, r: 0.35 },  // Siberia
  { lat: 40, lon: 60, r: 0.25 },   // Central Asia
  { lat: 15, lon: 105, r: 0.15 },  // Southeast Asia
  { lat: 35, lon: 135, r: 0.12 },  // Japan
  // Australia
  { lat: -25, lon: 135, r: 0.25 },
  { lat: -20, lon: 125, r: 0.15 },
  // Antarctica (partial)
  { lat: -80, lon: 0, r: 0.30 },
  { lat: -75, lon: 90, r: 0.25 },
  // Greenland
  { lat: 72, lon: -40, r: 0.15 },
];

// Mountain ranges: narrower caps with extra peak height
const EARTH_MOUNTAIN_CAPS: MountainCap[] = [
  { lat: 28, lon: 87, r: 0.08, peak: 0.8 },    // Himalayas
  { lat: 35, lon: 75, r: 0.06, peak: 0.5 },     // Hindu Kush
  { lat: -15, lon: -68, r: 0.04, peak: 0.6 },    // Andes (central)
  { lat: -30, lon: -70, r: 0.04, peak: 0.5 },    // Andes (south)
  { lat: 0, lon: -78, r: 0.03, peak: 0.5 },      // Andes (north)
  { lat: 45, lon: -115, r: 0.06, peak: 0.4 },    // Rockies
  { lat: 46, lon: 8, r: 0.04, peak: 0.4 },       // Alps
  { lat: -5, lon: 37, r: 0.03, peak: 0.3 },      // Kilimanjaro area
  { lat: 42, lon: 44, r: 0.03, peak: 0.3 },      // Caucasus
];

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function latLonToDir(latDeg: number, lonDeg: number): [number, number, number] {
  const lat = latDeg * Math.PI / 180;
  const lon = lonDeg * Math.PI / 180;
  return [
    dcos(lat) * dcos(lon),
    dsin(lat),
    dcos(lat) * dsin(lon),
  ];
}

export function generateEarthHeightmap(
  basePositions: Float32Array,
  radius: number,
  rng: SeededRandom
): Float32Array {
  const vertexCount = basePositions.length / 3;
  const heightmap = new Float32Array(vertexCount);

  // Precompute land cap directions
  const landDirs = EARTH_LAND_CAPS.map(c => ({
    dir: latLonToDir(c.lat, c.lon),
    r: c.r,
  }));
  const mtDirs = EARTH_MOUNTAIN_CAPS.map(c => ({
    dir: latLonToDir(c.lat, c.lon),
    r: c.r,
    peak: c.peak,
  }));

  // RNG-driven octave offsets for fine noise
  const noiseOffsets: { ox: number; oy: number; oz: number }[] = [];
  for (let o = 0; o < 4; o++) {
    noiseOffsets.push({
      ox: rng.range(-100, 100),
      oy: rng.range(-100, 100),
      oz: rng.range(-100, 100),
    });
  }

  const COAST_WIDTH = 0.05; // radians of smoothstep transition
  const LAND_BASE = 0.03;   // base land height as fraction of radius
  const OCEAN_BASE = -0.02;  // ocean depth as fraction of radius
  const MT_SCALE = 0.08;     // mountain extra height as fraction of radius

  for (let i = 0; i < vertexCount; i++) {
    const nx = basePositions[i * 3] / radius;
    const ny = basePositions[i * 3 + 1] / radius;
    const nz = basePositions[i * 3 + 2] / radius;

    // Compute land proximity: max smoothstep across all land caps
    let landness = 0;
    for (const cap of landDirs) {
      const dot = nx * cap.dir[0] + ny * cap.dir[1] + nz * cap.dir[2];
      const angle = dacos(Math.max(-1, Math.min(1, dot)));
      // Inside cap → 1, outside → 0, smooth transition at edge
      const contribution = 1 - smoothstep(cap.r - COAST_WIDTH, cap.r + COAST_WIDTH, angle);
      if (contribution > landness) landness = contribution;
    }

    // Mountain contribution
    let mountainHeight = 0;
    for (const mt of mtDirs) {
      const dot = nx * mt.dir[0] + ny * mt.dir[1] + nz * mt.dir[2];
      const angle = dacos(Math.max(-1, Math.min(1, dot)));
      const falloff = dexp(-(angle / mt.r) * (angle / mt.r) * 0.5);
      mountainHeight = Math.max(mountainHeight, falloff * mt.peak * MT_SCALE * radius);
    }

    // Base height: land vs ocean
    const baseHeight = landness * LAND_BASE * radius + (1 - landness) * OCEAN_BASE * radius;

    // Fine noise (4 octaves, seed-dependent)
    let noise = 0;
    const freqs = [3.0, 6.0, 12.0, 24.0];
    const amps = [0.012, 0.006, 0.003, 0.0015];
    for (let o = 0; o < 4; o++) {
      const { ox, oy, oz } = noiseOffsets[o];
      const f = freqs[o];
      noise += dsin((nx + ox) * f + (ny + oy) * (f * 0.7)) * amps[o];
      noise += dsin((nz + oz) * f * 1.3 + (nx + ox) * (f * 0.4)) * amps[o] * 0.5;
    }
    noise *= radius;

    // Combine: base + mountains (only on land) + noise
    heightmap[i] = baseHeight + mountainHeight * landness + noise;
  }

  return heightmap;
}

// =============================================================================
// TERRAIN STAMPS — geological feature overlays
// =============================================================================

interface PlacedStamp {
  type: TerrainStampType;
  center: [number, number, number]; // unit direction
  angularSize: number;
  amplitude: number;
  // For path-based stamps (valley, canyon, ridge): second endpoint direction
  endDir?: [number, number, number];
  // Valley variation: width/depth modulation along the arc
  valleyVariation?: {
    widthScale: number;       // multiplier on base half-width (0.6–1.4)
    asymmetry: number;        // left-right asymmetry (-0.4 to 0.4)
    // Per-segment depth multipliers (4 samples along arc, lerped between)
    depthSamples: number[];
    meander: number;          // lateral sinusoidal wobble amplitude in radians
    meanderFreq: number;      // wobble frequency
  };
  // For spire clusters: sub-peak offsets
  subPeaks?: { dir: [number, number, number]; scale: number }[];
}

function randomUnitDir(rng: SeededRandom): [number, number, number] {
  const theta = rng.range(0, Math.PI * 2);
  const cosPhi = rng.range(-1, 1);
  const sinPhi = Math.sqrt(1 - cosPhi * cosPhi);
  return [sinPhi * dcos(theta), sinPhi * dsin(theta), cosPhi];
}

function placeStamps(stamps: TerrainStampConfig[], rng: SeededRandom, radius: number): PlacedStamp[] {
  const placed: PlacedStamp[] = [];
  for (const cfg of stamps) {
    const count = rng.int(cfg.count[0], cfg.count[1]);
    for (let i = 0; i < count; i++) {
      const center = randomUnitDir(rng);
      const angularSize = rng.range(cfg.angularSize[0], cfg.angularSize[1]);
      const amplitude = rng.range(cfg.amplitude[0], cfg.amplitude[1]) * radius;

      const stamp: PlacedStamp = { type: cfg.type, center, angularSize, amplitude };

      // Path-based stamps need a second endpoint
      if (cfg.type === 'valley' || cfg.type === 'canyon' || cfg.type === 'ridge') {
        // Generate endpoint at roughly angularSize distance from center along random great circle
        const arcLen = angularSize * 2;
        // Build tangent frame at center
        const [cx, cy, cz] = center;
        const arbUp = Math.abs(cy) < 0.9 ? [0, 1, 0] : [1, 0, 0];
        // tangent = normalize(arbUp × center)
        const tx = arbUp[1] * cz - arbUp[2] * cy;
        const ty = arbUp[2] * cx - arbUp[0] * cz;
        const tz = arbUp[0] * cy - arbUp[1] * cx;
        const tLen = Math.sqrt(tx * tx + ty * ty + tz * tz) || 1;
        const t = [tx / tLen, ty / tLen, tz / tLen];
        // bitangent = center × t
        const bx = cy * t[2] - cz * t[1];
        const by = cz * t[0] - cx * t[2];
        const bz = cx * t[1] - cy * t[0];

        const angle = rng.range(0, Math.PI * 2);
        const dirX = t[0] * dcos(angle) + bx * dsin(angle);
        const dirY = t[1] * dcos(angle) + by * dsin(angle);
        const dirZ = t[2] * dcos(angle) + bz * dsin(angle);

        // Rotate center toward dir by arcLen
        const cosA = dcos(arcLen);
        const sinA = dsin(arcLen);
        const ex = cx * cosA + dirX * sinA;
        const ey = cy * cosA + dirY * sinA;
        const ez = cz * cosA + dirZ * sinA;
        const eLen = Math.sqrt(ex * ex + ey * ey + ez * ez) || 1;
        stamp.endDir = [ex / eLen, ey / eLen, ez / eLen];

        // Valley variation: width modulation, asymmetry, depth variation, meander
        if (cfg.type === 'valley') {
          stamp.valleyVariation = {
            widthScale: rng.range(0.6, 1.4),
            asymmetry: rng.range(-0.4, 0.4),
            depthSamples: [
              rng.range(0.3, 1.0),  // start
              rng.range(0.5, 1.0),  // 1/3
              rng.range(0.5, 1.0),  // 2/3
              rng.range(0.3, 1.0),  // end
            ],
            meander: rng.range(0.02, 0.12),
            meanderFreq: rng.range(2, 5),
          };
        }
      }

      // Spire clusters: generate sub-peaks
      if (cfg.type === 'spireCluster') {
        const numSpires = rng.int(3, 6);
        stamp.subPeaks = [];
        for (let s = 0; s < numSpires; s++) {
          // Offset from center within angular size
          const offsetAngle = rng.range(0, Math.PI * 2);
          const offsetDist = rng.range(0, angularSize * 0.8);
          const [cx, cy, cz] = center;
          const arbUp = Math.abs(cy) < 0.9 ? [0, 1, 0] : [1, 0, 0];
          const tx = arbUp[1] * cz - arbUp[2] * cy;
          const ty = arbUp[2] * cx - arbUp[0] * cz;
          const tz = arbUp[0] * cy - arbUp[1] * cx;
          const tLen = Math.sqrt(tx * tx + ty * ty + tz * tz) || 1;
          const tN = [tx / tLen, ty / tLen, tz / tLen];
          const bx = cy * tN[2] - cz * tN[1];
          const by = cz * tN[0] - cx * tN[2];
          const bz = cx * tN[1] - cy * tN[0];

          const dx = tN[0] * dcos(offsetAngle) + bx * dsin(offsetAngle);
          const dy = tN[1] * dcos(offsetAngle) + by * dsin(offsetAngle);
          const dz = tN[2] * dcos(offsetAngle) + bz * dsin(offsetAngle);

          const cosD = dcos(offsetDist);
          const sinD = dsin(offsetDist);
          const px = cx * cosD + dx * sinD;
          const py = cy * cosD + dy * sinD;
          const pz = cz * cosD + dz * sinD;
          const pLen = Math.sqrt(px * px + py * py + pz * pz) || 1;

          stamp.subPeaks.push({
            dir: [px / pLen, py / pLen, pz / pLen],
            scale: rng.range(0.4, 1.0),
          });
        }
      }

      placed.push(stamp);
    }
  }
  return placed;
}

function angularDist(ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
  const dot = ax * bx + ay * by + az * bz;
  return dacos(Math.max(-1, Math.min(1, dot)));
}

/** Perpendicular angular distance from point to great-circle arc between a and b */
function perpDistToArc(
  px: number, py: number, pz: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number
): number {
  // Normal to the great circle plane = a × b
  const nx = ay * bz - az * by;
  const ny = az * bx - ax * bz;
  const nz = ax * by - ay * bx;
  const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
  // Signed distance = asin(dot(p, normal))
  const sinDist = (px * nx + py * ny + pz * nz) / nLen;
  return Math.abs(dasin(Math.max(-1, Math.min(1, sinDist))));
}

function applyStamps(
  stamps: PlacedStamp[],
  finalVertices: number[],
  heightmap: Float32Array,
  vertexCount: number,
  radius: number
): void {
  for (const stamp of stamps) {
    const [cx, cy, cz] = stamp.center;
    const size = stamp.angularSize;
    const amp = stamp.amplitude;

    for (let i = 0; i < vertexCount; i++) {
      const vx = finalVertices[i * 3];
      const vy = finalVertices[i * 3 + 1];
      const vz = finalVertices[i * 3 + 2];

      let delta = 0;

      switch (stamp.type) {
        case 'caldera': {
          const dist = angularDist(vx, vy, vz, cx, cy, cz);
          if (dist < size) {
            const t = dist / size;
            // Inner depression (t < 0.4), rim peak (t ~ 0.7), falloff beyond
            if (t < 0.4) {
              delta = -amp * 0.6 * (1 - (t / 0.4) * (t / 0.4));
            } else if (t < 0.7) {
              const rimT = (t - 0.4) / 0.3;
              delta = -amp * 0.6 * (1 - rimT) * (1 - rimT) * (-1) + amp * rimT * rimT;
              // Interpolate from depression edge to rim peak
              delta = amp * rimT * rimT - amp * 0.6 * (1 - rimT) * (1 - rimT) * 0;
              const depthAtEdge = 0; // at t=0.4 depression is 0
              delta = depthAtEdge + (amp - depthAtEdge) * rimT;
            } else {
              const fallT = (t - 0.7) / 0.3;
              delta = amp * (1 - fallT * fallT);
            }
          }
          break;
        }
        case 'valley': {
          if (!stamp.endDir) break;
          const [ex, ey, ez] = stamp.endDir;
          const vv = stamp.valleyVariation;
          const baseHalfWidth = size * 0.3 * (vv ? vv.widthScale : 1);
          const distA = angularDist(vx, vy, vz, cx, cy, cz);
          const distB = angularDist(vx, vy, vz, ex, ey, ez);
          const arcLen = angularDist(cx, cy, cz, ex, ey, ez);
          // Along-arc parameter: 0 at center, 1 at endDir
          const alongT = arcLen > 0 ? Math.max(0, Math.min(1, (distA * distA - distB * distB + arcLen * arcLen) / (2 * arcLen * arcLen))) : 0;

          // Meander: shift the effective centerline laterally
          let effectivePerpDist = perpDistToArc(vx, vy, vz, cx, cy, cz, ex, ey, ez);
          if (vv) {
            const meanderShift = vv.meander * dsin(alongT * vv.meanderFreq * Math.PI);
            effectivePerpDist = Math.abs(effectivePerpDist - meanderShift);
          }

          // Width varies along arc: wider in middle, narrower at ends
          const widthMult = 0.7 + 0.3 * dsin(alongT * Math.PI);
          const halfWidth = baseHalfWidth * widthMult;

          if (effectivePerpDist < halfWidth && distA < arcLen + halfWidth && distB < arcLen + halfWidth) {
            const t = effectivePerpDist / halfWidth;

            // Asymmetric U-profile: one wall steeper than the other
            let profile: number;
            if (vv) {
              const rawPerp = perpDistToArc(vx, vy, vz, cx, cy, cz, ex, ey, ez);
              const side = rawPerp >= 0 ? 1 : -1; // which side of centerline
              const steepness = 1 + side * vv.asymmetry; // 0.6–1.4
              profile = 1 - dpow(t, 1.5 + 0.5 * steepness);
            } else {
              profile = 1 - t * t;
            }

            // Depth varies along arc via depthSamples (cubic interpolation)
            let depthMult = 1;
            if (vv) {
              const ds = vv.depthSamples;
              const si = alongT * 3; // 0–3 across 4 samples
              const idx = Math.min(2, Math.floor(si));
              const frac = si - idx;
              depthMult = ds[idx] * (1 - frac) + ds[idx + 1] * frac;
            }

            // Taper at arc ends
            const minDist = Math.min(distA, distB);
            const endTaper = minDist > arcLen ? Math.max(0, 1 - (minDist - arcLen) / halfWidth) : 1;
            delta = -amp * profile * depthMult * endTaper;
          }
          break;
        }
        case 'canyon': {
          if (!stamp.endDir) break;
          const [ex, ey, ez] = stamp.endDir;
          const perpDist = perpDistToArc(vx, vy, vz, cx, cy, cz, ex, ey, ez);
          const halfWidth = size * 0.15; // narrower than valley
          if (perpDist < halfWidth) {
            const distA = angularDist(vx, vy, vz, cx, cy, cz);
            const distB = angularDist(vx, vy, vz, ex, ey, ez);
            const arcLen = angularDist(cx, cy, cz, ex, ey, ez);
            if (distA < arcLen + halfWidth && distB < arcLen + halfWidth) {
              // V-shaped profile
              const t = perpDist / halfWidth;
              const profile = 1 - t; // linear V
              const minDist = Math.min(distA, distB);
              const endTaper = minDist > arcLen ? Math.max(0, 1 - (minDist - arcLen) / halfWidth) : 1;
              delta = -amp * 1.2 * profile * endTaper;
            }
          }
          break;
        }
        case 'crater': {
          const dist = angularDist(vx, vy, vz, cx, cy, cz);
          if (dist < size) {
            const t = dist / size;
            if (t < 0.85) {
              // Inverted parabolic bowl
              delta = -amp * (1 - (t / 0.85) * (t / 0.85));
            } else {
              // Raised rim
              const rimT = (t - 0.85) / 0.15;
              delta = amp * 0.4 * dsin(rimT * Math.PI);
            }
          }
          break;
        }
        case 'mesa': {
          const dist = angularDist(vx, vy, vz, cx, cy, cz);
          if (dist < size) {
            const t = dist / size;
            if (t < 0.7) {
              // Flat top
              delta = amp;
            } else {
              // Steep linear dropoff
              const fallT = (t - 0.7) / 0.3;
              delta = amp * (1 - fallT);
            }
          }
          break;
        }
        case 'ridge': {
          if (!stamp.endDir) break;
          const [ex, ey, ez] = stamp.endDir;
          const perpDist = perpDistToArc(vx, vy, vz, cx, cy, cz, ex, ey, ez);
          const width = size * 0.25;
          if (perpDist < width * 3) {
            const distA = angularDist(vx, vy, vz, cx, cy, cz);
            const distB = angularDist(vx, vy, vz, ex, ey, ez);
            const arcLen = angularDist(cx, cy, cz, ex, ey, ez);
            if (distA < arcLen + width && distB < arcLen + width) {
              // Gaussian perpendicular profile
              const t = perpDist / width;
              const profile = dexp(-t * t * 0.5);
              const minDist = Math.min(distA, distB);
              const endTaper = minDist > arcLen ? Math.max(0, 1 - (minDist - arcLen) / width) : 1;
              delta = amp * profile * endTaper;
            }
          }
          break;
        }
        case 'basin': {
          const dist = angularDist(vx, vy, vz, cx, cy, cz);
          if (dist < size) {
            // Gentle parabolic depression
            const t = dist / size;
            delta = -amp * (1 - t * t);
          }
          break;
        }
        case 'spireCluster': {
          if (!stamp.subPeaks) break;
          for (const peak of stamp.subPeaks) {
            const dist = angularDist(vx, vy, vz, peak.dir[0], peak.dir[1], peak.dir[2]);
            const spireWidth = size * 0.15; // narrow peaks
            if (dist < spireWidth * 3) {
              const t = dist / spireWidth;
              delta += amp * peak.scale * dexp(-t * t);
            }
          }
          break;
        }
      }

      heightmap[i] += delta;
    }
  }
}

// =============================================================================
// TERRAIN ANALYSIS — slope and elevation helpers for terrain-aware placement
// =============================================================================

export function computeVertexSlopes(
  basePositions: Float32Array,
  heightmap: Float32Array,
  radius: number
): Float32Array {
  const vertexCount = heightmap.length;
  const slopes = new Float32Array(vertexCount);

  // Process per-face (3 vertices per triangle)
  for (let face = 0; face < vertexCount / 3; face++) {
    const i0 = face * 3;
    const i1 = i0 + 1;
    const i2 = i0 + 2;

    // Get displaced positions
    const getPos = (i: number): [number, number, number] => {
      const bx = basePositions[i * 3];
      const by = basePositions[i * 3 + 1];
      const bz = basePositions[i * 3 + 2];
      const h = heightmap[i];
      const len = Math.sqrt(bx * bx + by * by + bz * bz) || radius;
      const scale = (len + h) / len;
      return [bx * scale, by * scale, bz * scale];
    };

    const p0 = getPos(i0);
    const p1 = getPos(i1);
    const p2 = getPos(i2);

    // Face normal via cross product of edges
    const e1x = p1[0] - p0[0], e1y = p1[1] - p0[1], e1z = p1[2] - p0[2];
    const e2x = p2[0] - p0[0], e2y = p2[1] - p0[1], e2z = p2[2] - p0[2];
    const fnx = e1y * e2z - e1z * e2y;
    const fny = e1z * e2x - e1x * e2z;
    const fnz = e1x * e2y - e1y * e2x;
    const fnLen = Math.sqrt(fnx * fnx + fny * fny + fnz * fnz) || 1;

    // For each vertex in face, compute slope = angle between face normal and radial direction
    for (const vi of [i0, i1, i2]) {
      const rx = basePositions[vi * 3];
      const ry = basePositions[vi * 3 + 1];
      const rz = basePositions[vi * 3 + 2];
      const rLen = Math.sqrt(rx * rx + ry * ry + rz * rz) || 1;
      const dot = (fnx * rx + fny * ry + fnz * rz) / (fnLen * rLen);
      const angle = dacos(Math.max(-1, Math.min(1, Math.abs(dot))));
      // Take max slope if vertex appears in multiple faces (overwrite with larger)
      if (angle > slopes[vi]) slopes[vi] = angle;
    }
  }

  return slopes;
}

export function computeNormalizedElevation(heightmap: Float32Array): Float32Array {
  const n = heightmap.length;
  const normalized = new Float32Array(n);
  let minH = Infinity, maxH = -Infinity;
  for (let i = 0; i < n; i++) {
    if (heightmap[i] < minH) minH = heightmap[i];
    if (heightmap[i] > maxH) maxH = heightmap[i];
  }
  const range = maxH - minH;
  if (range > 0) {
    for (let i = 0; i < n; i++) {
      normalized[i] = (heightmap[i] - minH) / range;
    }
  }
  return normalized;
}

// Terrain fitness scoring for placement
interface TerrainCache {
  slopes: Float32Array;
  elevations: Float32Array;
}

// Keyed by the planet's heightmap array, not its index: an index-keyed cache
// outlived its match, so the next match's flora and rocks were placed against
// the previous match's terrain (a second match in a session, or a replay, built
// a different world than a fresh load — and P2P peers could disagree).
let terrainCacheMap = new WeakMap<Float32Array, TerrainCache>();

function getTerrainCache(planet: PlanetState, _planetIndex: number): TerrainCache {
  let cache = terrainCacheMap.get(planet.heightmap);
  if (!cache) {
    cache = {
      slopes: computeVertexSlopes(planet.basePositions, planet.heightmap, planet.radius),
      elevations: computeNormalizedElevation(planet.heightmap),
    };
    terrainCacheMap.set(planet.heightmap, cache);
  }
  return cache;
}

export function clearTerrainCache(): void {
  terrainCacheMap = new WeakMap();
}

function findNearestVertex(planet: PlanetState, dir: Vec3): number {
  const vertexCount = planet.heightmap.length;
  let bestIdx = 0;
  let bestDot = -2;
  for (let i = 0; i < vertexCount; i++) {
    const bx = planet.basePositions[i * 3];
    const by = planet.basePositions[i * 3 + 1];
    const bz = planet.basePositions[i * 3 + 2];
    const len = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
    const dot = (bx / len) * dir.x + (by / len) * dir.y + (bz / len) * dir.z;
    if (dot > bestDot) {
      bestDot = dot;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/** Score terrain fitness for rock placement */
function rockTerrainFitness(rockType: RockType, slope: number, elevation: number): number {
  switch (rockType) {
    case 'basalt':
      // Steep slopes, high elevation
      return Math.min(1, (slope / 0.5) * 0.6 + elevation * 0.4);
    case 'alabaster':
      // Flat elevated plateaus
      return Math.min(1, (1 - slope / 0.5) * 0.5 + elevation * 0.5);
    case 'sandstone':
      // Mid-elevation gentle slopes
      return Math.min(1, (1 - Math.abs(elevation - 0.5) * 2) * 0.5 + (1 - slope / 0.5) * 0.3 + 0.2);
    default:
      return 0.5; // neutral
  }
}

/** Score terrain fitness for flora placement */
function floraTerrainFitness(floraType: FloraType, slope: number, elevation: number): number {
  const isTree = floraType === 'spirePine' || floraType === 'canopyOak' || floraType === 'frondPalm';
  if (isTree) {
    // Trees prefer flat terrain, mid elevation
    const flatScore = Math.max(0, 1 - slope / 0.3);
    const elevScore = 1 - Math.abs(elevation - 0.4) * 2;
    return Math.min(1, flatScore * 0.6 + Math.max(0, elevScore) * 0.4);
  }
  // Ground flora is permissive
  const gentleSlope = Math.max(0, 1 - slope / 0.6);
  return Math.min(1, gentleSlope * 0.4 + 0.6);
}

// =============================================================================
// HAZARD GENERATION
// =============================================================================

export function generateHazards(
  planets: PlanetState[],
  players: PlayerState[],
  rng: SeededRandom,
  hazardDensity: 'none' | 'some' | 'many'
): HazardState[] {
  const hazards: HazardState[] = [];
  const counts = HAZARD_COUNTS[hazardDensity];
  if (!counts || counts.max === 0) return hazards;

  let nextId = 0;

  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    if (planet.type !== 'planet') continue;

    const archetypeConfig = PLANET_ARCHETYPES[planet.archetype];
    if (!archetypeConfig || archetypeConfig.eligibleHazards.length === 0) continue;

    // Determine how many hazards to place
    const count = rng.int(counts.min, counts.max);
    if (count === 0) continue;

    // Collect player spawn dirs on this planet for avoidance
    const playerDirs: Vec3[] = players
      .filter(p => p.planetIndex === pi)
      .map(p => p.surfaceDir);

    for (let h = 0; h < count; h++) {
      // Pick a random hazard type from eligible list
      const hazardType = archetypeConfig.eligibleHazards[
        rng.int(0, archetypeConfig.eligibleHazards.length - 1)
      ];

      // Generate random surface direction, avoiding player spawns
      let surfaceDir: Vec3 | null = null;
      for (let attempt = 0; attempt < 50; attempt++) {
        const theta = rng.range(0, Math.PI * 2);
        const phi = rng.range(0.2, Math.PI - 0.2);
        const candidate: Vec3 = {
          x: dsin(phi) * dcos(theta),
          y: dcos(phi),
          z: dsin(phi) * dsin(theta),
        };

        // Check angular distance from all player spawns on this planet
        let tooClose = false;
        for (const pd of playerDirs) {
          const dot = candidate.x * pd.x + candidate.y * pd.y + candidate.z * pd.z;
          const angle = dacos(Math.max(-1, Math.min(1, dot)));
          if (angle < HAZARD_MIN_SPAWN_ANGLE) {
            tooClose = true;
            break;
          }
        }

        if (!tooClose) {
          surfaceDir = candidate;
          break;
        }
      }

      if (!surfaceDir) continue; // couldn't find valid placement

      hazards.push({
        id: nextId++,
        hazardType,
        planetIndex: pi,
        surfaceDir,
        health: HAZARD_HEALTH[hazardType],
        alive: true,
        scale: rng.range(0.6, 1.0),
      });
    }
  }

  return hazards;
}

// =============================================================================
// RUIN GENERATION — neutral pre-existing structures
// =============================================================================

/** Optional per-planet override for campaign/custom levels */
export interface RuinOverride {
  planetIndex: number;
  ruinType: RuinType;
  surfaceDir: Vec3;
}

/** Pick a ruin type using weighted random selection */
function pickWeightedRuin(weights: RuinWeight[], rng: SeededRandom): RuinType {
  let totalWeight = 0;
  for (const w of weights) totalWeight += w.weight;
  let roll = rng.range(0, totalWeight);
  for (const w of weights) {
    roll -= w.weight;
    if (roll <= 0) return w.type;
  }
  return weights[weights.length - 1].type;
}

export function generateRuins(
  planets: PlanetState[],
  players: PlayerState[],
  hazards: HazardState[],
  rng: SeededRandom,
  ruinDensity: 'none' | 'few' | 'some' | 'many' = 'some',
  overrides?: RuinOverride[]
): RuinState[] {
  const ruins: RuinState[] = [];
  let nextId = 0;

  // Place explicit overrides first (campaign/custom levels)
  if (overrides) {
    for (const ov of overrides) {
      ruins.push({
        id: nextId++,
        ruinType: ov.ruinType,
        planetIndex: ov.planetIndex,
        surfaceDir: ov.surfaceDir,
      });
    }
  }

  const counts = RUIN_COUNTS[ruinDensity];
  if (!counts || counts.max === 0) return ruins;

  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    if (planet.type !== 'planet') continue;

    const count = rng.int(counts.min, counts.max);
    if (count === 0) continue;

    // Get archetype-driven eligible ruin types (fall back to uniform all)
    const archetypeConfig = PLANET_ARCHETYPES[planet.archetype];
    const weights: RuinWeight[] = (archetypeConfig && archetypeConfig.eligibleRuins.length > 0)
      ? archetypeConfig.eligibleRuins
      : ALL_RUIN_TYPES;

    // Collect occupied directions (players + hazards + already-placed ruins) for avoidance
    const occupiedDirs: Vec3[] = [
      ...players.filter(p => p.planetIndex === pi).map(p => p.surfaceDir),
      ...hazards.filter(h => h.planetIndex === pi).map(h => h.surfaceDir),
      ...ruins.filter(r => r.planetIndex === pi).map(r => r.surfaceDir),
    ];

    for (let r = 0; r < count; r++) {
      const ruinType = pickWeightedRuin(weights, rng);

      // Find placement avoiding occupied spots
      let surfaceDir: Vec3 | null = null;
      for (let attempt = 0; attempt < 50; attempt++) {
        const theta = rng.range(0, Math.PI * 2);
        const phi = rng.range(0.2, Math.PI - 0.2);
        const candidate: Vec3 = {
          x: dsin(phi) * dcos(theta),
          y: dcos(phi),
          z: dsin(phi) * dsin(theta),
        };

        let tooClose = false;
        for (const od of occupiedDirs) {
          const dot = candidate.x * od.x + candidate.y * od.y + candidate.z * od.z;
          const angle = dacos(Math.max(-1, Math.min(1, dot)));
          if (angle < RUIN_MIN_SPAWN_ANGLE) {
            tooClose = true;
            break;
          }
        }

        if (!tooClose) {
          surfaceDir = candidate;
          break;
        }
      }

      if (!surfaceDir) continue;

      // Add to occupied so subsequent ruins on same planet don't overlap
      occupiedDirs.push(surfaceDir);

      ruins.push({
        id: nextId++,
        ruinType,
        planetIndex: pi,
        surfaceDir,
      });
    }
  }

  return ruins;
}

// =============================================================================
// FLORA & FAUNA GENERATION
// =============================================================================

/** Pick a flora type using weighted random selection */
function pickWeightedFlora(weights: FloraWeight[], rng: SeededRandom): FloraType {
  let totalWeight = 0;
  for (const w of weights) totalWeight += w.weight;
  let roll = rng.range(0, totalWeight);
  for (const w of weights) {
    roll -= w.weight;
    if (roll <= 0) return w.type;
  }
  return weights[weights.length - 1].type;
}

const TREE_FLORA_TYPES = new Set<FloraType>(['spirePine', 'canopyOak', 'frondPalm']);
const STAND_SPREAD_ANGLE = 0.15; // radians — how far apart trees in a stand can be

export function generateFlora(
  planets: PlanetState[],
  players: PlayerState[],
  hazards: HazardState[],
  ruins: RuinState[],
  rng: SeededRandom,
  floraDensity: 'none' | 'some' | 'many' = 'some'
): FloraState[] {
  const flora: FloraState[] = [];
  let nextId = 0;

  const counts = FLORA_COUNTS[floraDensity];
  if (!counts || counts.max === 0) return flora;

  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    if (planet.type !== 'planet') continue;

    const archetypeConfig = PLANET_ARCHETYPES[planet.archetype];
    if (!archetypeConfig || archetypeConfig.eligibleFlora.length === 0) continue;

    const count = rng.int(counts.min, counts.max);
    if (count === 0) continue;

    // Collect occupied directions for avoidance (non-flora objects)
    const occupiedDirs: Vec3[] = [
      ...players.filter(p => p.planetIndex === pi).map(p => p.surfaceDir),
      ...hazards.filter(h => h.planetIndex === pi).map(h => h.surfaceDir),
      ...ruins.filter(r => r.planetIndex === pi).map(r => r.surfaceDir),
      ...flora.filter(f => f.planetIndex === pi).map(f => f.surfaceDir),
    ];

    for (let f = 0; f < count; f++) {
      const floraType = pickWeightedFlora(archetypeConfig.eligibleFlora, rng);
      const isTree = TREE_FLORA_TYPES.has(floraType);

      let surfaceDir: Vec3 | null = null;
      for (let attempt = 0; attempt < 50; attempt++) {
        const theta = rng.range(0, Math.PI * 2);
        const phi = rng.range(0.2, Math.PI - 0.2);
        const candidate: Vec3 = {
          x: dsin(phi) * dcos(theta),
          y: dcos(phi),
          z: dsin(phi) * dsin(theta),
        };

        let tooClose = false;
        for (const od of occupiedDirs) {
          const dot = candidate.x * od.x + candidate.y * od.y + candidate.z * od.z;
          const angle = dacos(Math.max(-1, Math.min(1, dot)));
          if (angle < FLORA_MIN_SPAWN_ANGLE) {
            tooClose = true;
            break;
          }
        }

        if (!tooClose) {
          // Terrain fitness check
          const terrain = getTerrainCache(planet, pi);
          const vi = findNearestVertex(planet, candidate);
          const score = floraTerrainFitness(floraType, terrain.slopes[vi], terrain.elevations[vi]);
          if (rng.range(0, 1) < 0.2 + 0.8 * score) {
            surfaceDir = candidate;
            break;
          }
        }
      }

      if (!surfaceDir) continue;

      occupiedDirs.push(surfaceDir);

      flora.push({
        id: nextId++,
        floraType,
        planetIndex: pi,
        surfaceDir,
        alive: true,
        scale: rng.range(0.7, 1.0),
      });

      // Trees form stands: clustered around the anchor
      // Pines form large groves (1-20), oaks/palms form smaller stands (1-7)
      if (isTree) {
        const standExtra = floraType === 'spirePine' ? rng.int(1, 20) : rng.int(1, 7);
        for (let s = 0; s < standExtra; s++) {
          // Perturb direction slightly from anchor using a tangent-space offset
          const offsetAngle = rng.range(0, Math.PI * 2);
          const offsetMag = rng.range(0.04, STAND_SPREAD_ANGLE);

          // Build a local tangent frame around surfaceDir
          const n = surfaceDir;
          const arbUp = Math.abs(n.y) < 0.9
            ? { x: 0, y: 1, z: 0 }
            : { x: 1, y: 0, z: 0 };
          // tangent = normalize(arbUp cross n)
          const tx = arbUp.y * n.z - arbUp.z * n.y;
          const ty = arbUp.z * n.x - arbUp.x * n.z;
          const tz = arbUp.x * n.y - arbUp.y * n.x;
          const tLen = Math.sqrt(tx * tx + ty * ty + tz * tz) || 1;
          const t = { x: tx / tLen, y: ty / tLen, z: tz / tLen };
          // bitangent = n cross t
          const bx = n.y * t.z - n.z * t.y;
          const by = n.z * t.x - n.x * t.z;
          const bz = n.x * t.y - n.y * t.x;

          // Offset in tangent plane, then re-normalize to sphere surface
          const dx = dcos(offsetAngle) * offsetMag;
          const dz = dsin(offsetAngle) * offsetMag;
          const cx = n.x + t.x * dx + bx * dz;
          const cy = n.y + t.y * dx + by * dz;
          const cz = n.z + t.z * dx + bz * dz;
          const cLen = Math.sqrt(cx * cx + cy * cy + cz * cz) || 1;
          const standDir: Vec3 = { x: cx / cLen, y: cy / cLen, z: cz / cLen };

          flora.push({
            id: nextId++,
            floraType,
            planetIndex: pi,
            surfaceDir: standDir,
            alive: true,
            scale: rng.range(0.6, 1.0),
          });
        }
      }
    }
  }

  return flora;
}

// =============================================================================
// ROCK GENERATION
// =============================================================================

/** Pick a rock type using weighted random selection */
function pickWeightedRock(weights: RockWeight[], rng: SeededRandom): RockType {
  let totalWeight = 0;
  for (const w of weights) totalWeight += w.weight;
  let roll = rng.range(0, totalWeight);
  for (const w of weights) {
    roll -= w.weight;
    if (roll <= 0) return w.type;
  }
  return weights[weights.length - 1].type;
}

const ROCK_MODE_WEIGHTS: { mode: RockState['mode']; weight: number }[] = [
  { mode: 'small', weight: 40 },
  { mode: 'medium', weight: 25 },
  { mode: 'large', weight: 15 },
  { mode: 'cluster', weight: 12 },
  { mode: 'outcrop', weight: 8 },
];

function pickRockMode(rng: SeededRandom): RockState['mode'] {
  let total = 0;
  for (const w of ROCK_MODE_WEIGHTS) total += w.weight;
  let roll = rng.range(0, total);
  for (const w of ROCK_MODE_WEIGHTS) {
    roll -= w.weight;
    if (roll <= 0) return w.mode;
  }
  return 'small';
}

export function generateRocks(
  planets: PlanetState[],
  players: PlayerState[],
  hazards: HazardState[],
  ruins: RuinState[],
  flora: FloraState[],
  rng: SeededRandom,
  rockDensity: 'none' | 'some' | 'many' = 'some'
): RockState[] {
  const rocks: RockState[] = [];
  let nextId = 0;

  const counts = ROCK_COUNTS[rockDensity];
  if (!counts || counts.max === 0) return rocks;

  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    if (planet.type !== 'planet') continue;

    const archetypeConfig = PLANET_ARCHETYPES[planet.archetype];
    if (!archetypeConfig || archetypeConfig.eligibleRocks.length === 0) continue;

    const count = rng.int(counts.min, counts.max);
    if (count === 0) continue;

    const occupiedDirs: Vec3[] = [
      ...players.filter(p => p.planetIndex === pi).map(p => p.surfaceDir),
      ...hazards.filter(h => h.planetIndex === pi).map(h => h.surfaceDir),
      ...ruins.filter(r => r.planetIndex === pi).map(r => r.surfaceDir),
      ...flora.filter(f => f.planetIndex === pi).map(f => f.surfaceDir),
      ...rocks.filter(r => r.planetIndex === pi).map(r => r.surfaceDir),
    ];

    for (let ri = 0; ri < count; ri++) {
      const rockType = pickWeightedRock(archetypeConfig.eligibleRocks, rng);
      const mode = pickRockMode(rng);

      let surfaceDir: Vec3 | null = null;
      for (let attempt = 0; attempt < 50; attempt++) {
        const theta = rng.range(0, Math.PI * 2);
        const phi = rng.range(0.2, Math.PI - 0.2);
        const candidate: Vec3 = {
          x: dsin(phi) * dcos(theta),
          y: dcos(phi),
          z: dsin(phi) * dsin(theta),
        };

        let tooClose = false;
        for (const od of occupiedDirs) {
          const dot = candidate.x * od.x + candidate.y * od.y + candidate.z * od.z;
          const angle = dacos(Math.max(-1, Math.min(1, dot)));
          if (angle < ROCK_MIN_SPAWN_ANGLE) {
            tooClose = true;
            break;
          }
        }

        if (!tooClose) {
          // Terrain fitness check
          const terrain = getTerrainCache(planet, pi);
          const vi = findNearestVertex(planet, candidate);
          const score = rockTerrainFitness(rockType, terrain.slopes[vi], terrain.elevations[vi]);
          if (rng.range(0, 1) < 0.2 + 0.8 * score) {
            surfaceDir = candidate;
            break;
          }
        }
      }

      if (!surfaceDir) continue;

      occupiedDirs.push(surfaceDir);

      rocks.push({
        id: nextId++,
        rockType,
        mode,
        planetIndex: pi,
        surfaceDir,
        alive: true,
        scale: rng.range(0.7, 1.0),
        seed: rng.int(0, 99999),
      });
    }
  }

  clearTerrainCache();
  return rocks;
}

// =============================================================================
// ENVIRONMENTAL EFFECT GENERATION
// =============================================================================

/** Pick an effect type using weighted random selection */
function pickWeightedEffect(weights: EffectWeight[], rng: SeededRandom): EnvironmentalEffectType {
  let totalWeight = 0;
  for (const w of weights) totalWeight += w.weight;
  let roll = rng.range(0, totalWeight);
  for (const w of weights) {
    roll -= w.weight;
    if (roll <= 0) return w.type;
  }
  return weights[weights.length - 1].type;
}

export function generateEnvironmentalEffects(
  planets: PlanetState[],
  players: PlayerState[],
  hazards: HazardState[],
  ruins: RuinState[],
  flora: FloraState[],
  rng: SeededRandom,
  effectDensity: 'none' | 'some' | 'many' = 'some'
): EnvironmentalEffectState[] {
  const effects: EnvironmentalEffectState[] = [];
  let nextId = 0;

  const counts = EFFECT_COUNTS[effectDensity];

  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    if (planet.type !== 'planet') continue;

    const archetypeConfig = PLANET_ARCHETYPES[planet.archetype];

    // Archetype-based effects
    if (archetypeConfig && archetypeConfig.eligibleEffects.length > 0 && counts && counts.max > 0) {
      const count = rng.int(counts.min, counts.max);

      // Collect occupied directions for surface-based effect placement
      const occupiedDirs: Vec3[] = [
        ...players.filter(p => p.planetIndex === pi).map(p => p.surfaceDir),
        ...hazards.filter(h => h.planetIndex === pi).map(h => h.surfaceDir),
        ...ruins.filter(r => r.planetIndex === pi).map(r => r.surfaceDir),
        ...flora.filter(f => f.planetIndex === pi).map(f => f.surfaceDir),
      ];

      for (let e = 0; e < count; e++) {
        const effectType = pickWeightedEffect(archetypeConfig.eligibleEffects, rng);

        // Planet-scope effects (aurora) don't need surface placement
        const isPlanetScope = effectType === 'aurora';

        let surfaceDir: Vec3;
        if (isPlanetScope) {
          surfaceDir = { x: 0, y: 1, z: 0 };
        } else {
          let placed: Vec3 | null = null;
          for (let attempt = 0; attempt < 50; attempt++) {
            const theta = rng.range(0, Math.PI * 2);
            const phi = rng.range(0.2, Math.PI - 0.2);
            const candidate: Vec3 = {
              x: dsin(phi) * dcos(theta),
              y: dcos(phi),
              z: dsin(phi) * dsin(theta),
            };

            let tooClose = false;
            for (const od of occupiedDirs) {
              const dot = candidate.x * od.x + candidate.y * od.y + candidate.z * od.z;
              const angle = dacos(Math.max(-1, Math.min(1, dot)));
              if (angle < FLORA_MIN_SPAWN_ANGLE) {
                tooClose = true;
                break;
              }
            }

            if (!tooClose) {
              placed = candidate;
              break;
            }
          }

          if (!placed) continue;
          occupiedDirs.push(placed);
          surfaceDir = placed;
        }

        effects.push({
          id: nextId++,
          effectType,
          planetIndex: pi,
          surfaceDir,
          alive: true,
        });
      }
    }

    // Planet rings: random chance per planet, independent of archetype effects
    if (rng.next() < PLANET_RING_CHANCE) {
      effects.push({
        id: nextId++,
        effectType: 'planetRings',
        planetIndex: pi,
        surfaceDir: { x: 0, y: 1, z: 0 },
        alive: true,
      });
    }
  }

  return effects;
}

// =============================================================================
// PLAYER GENERATION
// =============================================================================

function generatePlayers(
  numPlayers: number,
  numBots: number,
  planets: PlanetState[],
  rng: SeededRandom,
  playerNames?: string[],
  playerLoadouts?: PlayerLoadout[],
  botDifficulty: string = 'medium',
  botPersonality: string = 'balanced',
  botOverrides?: BotOverride[],
  teamIndices?: number[],
  unitsPerTeam: number = 1,
  botUnitsPerTeam: number = 1,
  numTeams?: number,
  humanTeams?: number,
  botTeams?: number,
  unitSlots?: UnitSlotConfig[][],
  startingCBP: number = 12,
  botStartingCBP?: number
): PlayerState[] {
  const players: PlayerState[] = [];
  const usedPlanets = new Set<number>();

  // Get indices of spawnable bodies (not stars or wormholes)
  const spawnableIndices: number[] = [];
  for (let idx = 0; idx < planets.length; idx++) {
    if (planets[idx].type === 'planet') {
      spawnableIndices.push(idx);
    }
  }

  // Determine team structure
  const hTeams = humanTeams ?? (numPlayers > numBots ? numPlayers - numBots : 1);
  const bTeams = botTeams ?? numBots;
  const totalTeams = numTeams ?? (hTeams + bTeams);

  // If legacy mode (1 unit per team), use old behavior
  if (unitsPerTeam === 1 && botUnitsPerTeam === 1 && !numTeams) {
    // Legacy path: each player is their own team (or uses teamIndices)
    for (let i = 0; i < numPlayers; i++) {
      let planetIndex = spawnableIndices[i % spawnableIndices.length];
      for (let j = 0; j < spawnableIndices.length; j++) {
        const idx = spawnableIndices[(i + j) % spawnableIndices.length];
        if (!usedPlanets.has(idx)) {
          planetIndex = idx;
          usedPlanets.add(idx);
          break;
        }
      }

      const theta = rng.range(0, Math.PI * 2);
      const phi = rng.range(0.3, Math.PI - 0.3);
      const surfaceDir: Vec3 = {
        x: dsin(phi) * dcos(theta),
        y: dcos(phi),
        z: dsin(phi) * dsin(theta),
      };

      const isBot = i >= numPlayers - numBots;
      const teamIndex = teamIndices?.[i] ?? i % TEAMS.length;
      const team = TEAMS[teamIndex];

      let name: string;
      if (playerNames && playerNames[i] && playerNames[i].trim()) {
        name = playerNames[i].trim();
      } else if (isBot) {
        name = `Bot ${i - (numPlayers - numBots) + 1}`;
      } else {
        name = team.name;
      }

      let botConfig: BotConfig | undefined;
      if (isBot) {
        const botIndex = i - (numPlayers - numBots);
        const override = botOverrides?.[botIndex];
        const diff = override?.difficulty && override.difficulty !== 'default'
          ? override.difficulty : botDifficulty;
        const pers = override?.personality && override.personality !== 'default'
          ? override.personality : botPersonality;
        botConfig = {
          skill: BOT_SKILL_LEVELS[diff] ?? BOT_SKILL_LEVELS.medium,
          personality: BOT_PRESETS[pers] ?? BOT_PRESETS.balanced,
        };
      }

      players.push({
        id: i,
        name,
        companyName: team.name,
        teamIndex,
        planetIndex,
        surfaceDir,
        health: 100,
        alive: true,
        isBot,
        colorIndex: i,
        cbp: isBot ? (botStartingCBP ?? startingCBP) : startingCBP,
        selectedWeapon: 'standard',
        loadout: playerLoadouts && playerLoadouts[i] ? playerLoadouts[i] : createSandboxLoadout(),
        movementRange: BASE_MOVEMENT_RANGE,
        kit: defaultUnitKit(),
        movementRemaining: BASE_MOVEMENT_RANGE,
        airborne: false,
        airbornePos: null,
        airborneVel: null,
        lastWormholeId: null,
        wormholeTransits: 0,
        lastAimAzimuth: 0,
        lastAimElevation: Math.PI / 4,
        lastAimPower: 50,
        shotsFired: 0,
        damageDealt: 0,
        kills: 0,
        botConfig,
        unitIndex: 0,
        flagsRemaining: STARTING_FLAGS,
        jumppackFuel: 0,
      });
    }
    return players;
  }

  // unitSlots path: per-unit type/config from roster screen
  if (unitSlots && unitSlots.length > 0) {
    let playerId = 0;

    for (let teamSlot = 0; teamSlot < unitSlots.length; teamSlot++) {
      const teamIndex = teamIndices?.[teamSlot] ?? teamSlot % TEAMS.length;
      const team = TEAMS[teamIndex];
      const slots = unitSlots[teamSlot];

      // Filter to active (non-none) slots
      const activeSlots = slots.filter(s => s.type !== 'none');
      if (activeSlots.length === 0) continue;

      // Assign planet for this team
      let planetIndex = spawnableIndices[teamSlot % spawnableIndices.length];
      for (let j = 0; j < spawnableIndices.length; j++) {
        const idx = spawnableIndices[(teamSlot + j) % spawnableIndices.length];
        if (!usedPlanets.has(idx)) {
          planetIndex = idx;
          usedPlanets.add(idx);
          break;
        }
      }

      // Base team name
      let baseName: string;
      if (playerNames && playerNames[teamSlot] && playerNames[teamSlot].trim()) {
        baseName = playerNames[teamSlot].trim();
      } else {
        baseName = team.name;
      }

      let unitIdx = 0;
      for (let slotIdx = 0; slotIdx < slots.length; slotIdx++) {
        const slot = slots[slotIdx];
        if (slot.type === 'none') continue;

        const isBot = slot.type === 'bot';

        // Spread units around the planet surface
        const theta = rng.range(0, Math.PI * 2);
        const phi = rng.range(0.3, Math.PI - 0.3);
        const thetaOffset = (unitIdx / activeSlots.length) * Math.PI * 2;
        const surfaceDir: Vec3 = {
          x: dsin(phi) * dcos(theta + thetaOffset),
          y: dcos(phi),
          z: dsin(phi) * dsin(theta + thetaOffset),
        };

        // Name: use slot's custom name, or auto-generate
        let unitName: string;
        if (slot.name && slot.name.trim()) {
          unitName = slot.name.trim();
        } else if (activeSlots.length > 1) {
          unitName = `${baseName} ${UNIT_SUFFIXES[slotIdx] ?? String(slotIdx)}`;
        } else {
          unitName = baseName;
        }

        // Bot config from per-slot settings
        let botConfig: BotConfig | undefined;
        if (isBot) {
          botConfig = {
            skill: BOT_SKILL_LEVELS[slot.difficulty] ?? BOT_SKILL_LEVELS.medium,
            personality: BOT_PRESETS[slot.personality] ?? BOT_PRESETS.balanced,
          };
        }

        const loadout = playerLoadouts && playerLoadouts[teamSlot]
          ? playerLoadouts[teamSlot]
          : createSandboxLoadout();

        const fitnessBonus = (slot.fitnessLevel ?? 0) * FITNESS_MOVEMENT_BONUS;
        const movRange = BASE_MOVEMENT_RANGE + fitnessBonus;

        players.push({
          id: playerId,
          name: unitName,
          companyName: team.name,
          teamIndex,
          planetIndex,
          surfaceDir,
          health: 100,
          alive: true,
          isBot,
          colorIndex: playerId,
          cbp: isBot ? (botStartingCBP ?? startingCBP) : startingCBP,
          selectedWeapon: 'standard',
          loadout,
          movementRange: movRange,
          movementRemaining: movRange,
          airborne: false,
          airbornePos: null,
          airborneVel: null,
          lastWormholeId: null,
          wormholeTransits: 0,
          lastAimAzimuth: 0,
          lastAimElevation: Math.PI / 4,
          lastAimPower: 50,
          shotsFired: 0,
          damageDealt: 0,
          kills: 0,
          botConfig,
          unitIndex: unitIdx,
          flagsRemaining: STARTING_FLAGS,
          jumppackFuel: 0,
          jumpBonus: (slot.jumpLevel ?? 0) * JUMP_HEIGHT_BONUS,
          kit: { ...defaultUnitKit(), traits: { fitness: slot.fitnessLevel ?? 0, jump: slot.jumpLevel ?? 0 } },
        });

        playerId++;
        unitIdx++;
      }
    }

    return players;
  }

  // Multi-unit team path (legacy): generate units grouped by team
  let playerId = 0;

  for (let teamSlot = 0; teamSlot < totalTeams; teamSlot++) {
    const isBot = teamSlot >= hTeams;
    const upt = isBot ? botUnitsPerTeam : unitsPerTeam;
    const teamIndex = teamIndices?.[teamSlot] ?? teamSlot % TEAMS.length;
    const team = TEAMS[teamIndex];

    // Assign planet for this team — all teammates share the same planet
    let planetIndex = spawnableIndices[teamSlot % spawnableIndices.length];
    for (let j = 0; j < spawnableIndices.length; j++) {
      const idx = spawnableIndices[(teamSlot + j) % spawnableIndices.length];
      if (!usedPlanets.has(idx)) {
        planetIndex = idx;
        usedPlanets.add(idx);
        break;
      }
    }

    // Base team name from playerNames or team default
    let baseName: string;
    if (playerNames && playerNames[teamSlot] && playerNames[teamSlot].trim()) {
      baseName = playerNames[teamSlot].trim();
    } else if (isBot) {
      baseName = team.name;
    } else {
      baseName = team.name;
    }

    // Bot config for bot teams
    let botConfig: BotConfig | undefined;
    if (isBot) {
      const botIndex = teamSlot - hTeams;
      const override = botOverrides?.[botIndex];
      const diff = override?.difficulty && override.difficulty !== 'default'
        ? override.difficulty : botDifficulty;
      const pers = override?.personality && override.personality !== 'default'
        ? override.personality : botPersonality;
      botConfig = {
        skill: BOT_SKILL_LEVELS[diff] ?? BOT_SKILL_LEVELS.medium,
        personality: BOT_PRESETS[pers] ?? BOT_PRESETS.balanced,
      };
    }

    for (let unitIdx = 0; unitIdx < upt; unitIdx++) {
      // Spread units around the planet surface
      const theta = rng.range(0, Math.PI * 2);
      const phi = rng.range(0.3, Math.PI - 0.3);
      // Offset each unit's theta to spread them out
      const thetaOffset = (unitIdx / upt) * Math.PI * 2;
      const surfaceDir: Vec3 = {
        x: dsin(phi) * dcos(theta + thetaOffset),
        y: dcos(phi),
        z: dsin(phi) * dsin(theta + thetaOffset),
      };

      // Unit naming: "CompanyName Alpha/Bravo/Charlie" when multi-unit, plain name when single
      const unitName = upt > 1
        ? `${baseName} ${UNIT_SUFFIXES[unitIdx] ?? String(unitIdx)}`
        : baseName;

      // Use team's loadout for all units on that team
      const loadout = playerLoadouts && playerLoadouts[teamSlot]
        ? playerLoadouts[teamSlot]
        : createSandboxLoadout();

      players.push({
        id: playerId,
        name: unitName,
        companyName: team.name,
        teamIndex,
        planetIndex,
        surfaceDir,
        health: 100,
        alive: true,
        isBot,
        colorIndex: playerId,
        cbp: isBot ? (botStartingCBP ?? startingCBP) : startingCBP,
        selectedWeapon: 'standard',
        loadout,
        movementRange: BASE_MOVEMENT_RANGE,
        kit: defaultUnitKit(),
        movementRemaining: BASE_MOVEMENT_RANGE,
        airborne: false,
        airbornePos: null,
        airborneVel: null,
        lastWormholeId: null,
        wormholeTransits: 0,
        lastAimAzimuth: 0,
        lastAimElevation: Math.PI / 4,
        lastAimPower: 50,
        shotsFired: 0,
        damageDealt: 0,
        kills: 0,
        botConfig,
        unitIndex: unitIdx,
        flagsRemaining: STARTING_FLAGS,
        jumppackFuel: 0,
      });

      playerId++;
    }
  }

  return players;
}

// =============================================================================
// CARRYABLE GENERATION (CTF)
// =============================================================================

/**
 * Generate carryable objects for a mode.
 * CTF: one navCore per team at their home planet.
 * Other modes: returns [].
 */
export function generateCarryables(
  planets: PlanetState[],
  modeConfig: ModeConfig,
  rng: SeededRandom
): CarryableObjectState[] {
  if (getModeRules(modeConfig).objective !== 'ctf') return [];

  const homeTeamPlanets = modeConfig.homeTeamPlanets;
  if (!homeTeamPlanets) return [];

  const carryables: CarryableObjectState[] = [];
  let nextId = 0;
  const seenTeams = new Set<number>();

  for (const [teamIndexStr, planetIndex] of Object.entries(homeTeamPlanets)) {
    const teamIndex = Number(teamIndexStr);
    if (seenTeams.has(teamIndex)) continue;  // dedup guard
    seenTeams.add(teamIndex);
    const planet = planets[planetIndex];
    if (!planet) continue;

    // Place nav core at a deterministic surface position
    const theta = rng.range(0, Math.PI * 2);
    const phi = rng.range(0.4, Math.PI - 0.4);
    const surfaceDir = {
      x: dsin(phi) * dcos(theta),
      y: dcos(phi),
      z: dsin(phi) * dsin(theta),
    };

    carryables.push({
      id: nextId++,
      type: 'navCore',
      teamIndex,
      planetIndex,
      surfaceDir,
      carriedBy: null,
      originPlanetIndex: planetIndex,
      originSurfaceDir: { ...surfaceDir },
      alive: true,
    });
  }

  return carryables;
}
